import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Ticket, BarChart3, LogOut, Plus, Search, Trash2, Power, PowerOff,
  Copy, CheckCircle, Menu, X, Shield, ChevronLeft, ChevronRight, Activity, Zap, Cpu, Coins,
  ArrowLeft, Calendar, Mail, MessageSquare, DollarSign, Download, AlertTriangle, Gauge, Monitor,
  Settings, Radio, TrendingUp, Terminal
} from 'lucide-react';

const formatCost = (n) => {
  if (!n || n === 0) return '$0.00';
  if (n < 0.01) return '<$0.01';
  return '$' + n.toFixed(2);
};

const formatTokens = (n) => {
  if (!n || n === 0) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(n);
};

let logo;
try {
  logo = require('./images/logo.png');
} catch (e) {
  logo = null;
}

const API_BASE = typeof window !== 'undefined'
  ? (window.location.hostname === 'chipdesigner.ai' || window.location.hostname.endsWith('.chipdesigner.ai')
    ? `${window.location.protocol}//${window.location.hostname}`
    : `http://${window.location.hostname}:8006`)
  : 'http://localhost:8006';

/* ── Animated primitives ───────────────────────────────────────── */

/** Number that counts up from 0 on mount (ease-out cubic). Uses
 *  formatTokens above 1000 unless `raw` is set. */
const CountUp = ({ value, prefix = '', suffix = '', decimals = 0, raw = false, className = '' }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const dur = 900;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setDisplay(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  const txt = raw
    ? display.toFixed(decimals)
    : (display >= 1000 ? formatTokens(display) : display.toFixed(decimals));
  return <span className={className}>{prefix}{txt}{suffix}</span>;
};

/** SVG line+area chart with draw-in animation. data: [{date, requests, cost_usd}] */
const AreaChart = ({ data }) => {
  if (!data?.length) return <p className="text-xs text-slate-500 self-center">No usage in window</p>;
  const W = 620, H = 140, PAD = 10;
  const maxR = Math.max(...data.map(d => d.requests), 1);
  const xs = data.map((_, i) => PAD + i * (W - 2 * PAD) / Math.max(1, data.length - 1));
  const ys = data.map(d => H - PAD - (d.requests / maxR) * (H - 2 * PAD - 14));
  const line = xs.map((x, i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const area = `${line} L${xs[xs.length - 1].toFixed(1)},${H - PAD} L${xs[0].toFixed(1)},${H - PAD} Z`;
  // horizontal gridlines at 25/50/75%
  const grid = [0.25, 0.5, 0.75].map(g => H - PAD - g * (H - 2 * PAD - 14));
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-36 overflow-visible" preserveAspectRatio="none">
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.38" />
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {grid.map((y, i) => (
          <line key={i} x1={PAD} x2={W - PAD} y1={y} y2={y} stroke="#1e293b" strokeWidth="1" strokeDasharray="4 4" />
        ))}
        <path d={area} fill="url(#areaGrad)" className="chart-area-fade" />
        <path d={line} fill="none" stroke="#22d3ee" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"
          pathLength="1" className="chart-line-draw" />
        {xs.map((x, i) => (
          <g key={i}>
            <circle cx={x} cy={ys[i]} r="10" fill="transparent">
              <title>{`${data[i].date} — ${data[i].requests} requests · $${data[i].cost_usd.toFixed(2)}`}</title>
            </circle>
            <circle cx={x} cy={ys[i]} r="3.5" fill="#0d111c" stroke="#22d3ee" strokeWidth="2" className="chart-dot" style={{ animationDelay: `${0.15 + i * 0.08}s` }}>
              <title>{`${data[i].date} — ${data[i].requests} requests · $${data[i].cost_usd.toFixed(2)}`}</title>
            </circle>
          </g>
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-slate-500 tabular-nums mt-1 px-1">
        {data.map(d => <span key={d.date}>{d.date.slice(5)}</span>)}
      </div>
    </div>
  );
};

const PLAN_BADGE = {
  free: 'bg-slate-700/40 text-slate-400',
  pro: 'bg-cyan-500/10 text-cyan-400',
  team: 'bg-purple-500/10 text-purple-400',
  enterprise: 'bg-amber-500/10 text-amber-400',
};

const KNOWN_MODELS = [
  'deepseek-v4-pro', 'deepseek-flash', 'kimi-k3', 'kimi-k2.7-code', 'kimi-k2.6',
  'kimi-auto', 'glm-4.5-flash', 'elinnos-free', 'gpt-4o', 'gpt-4o-mini',
  'claude-sonnet', 'claude-haiku', 'claude-opus', 'gemini-2-flash', 'gemini-2-pro',
  'chipgen-1.1', 'qwen3.8-27b',
];

/** Editable plan card — CREDIT limits (the real quota; 1 cre = $0.01 API
 *  cost) + token runaway backstop + model gating via
 *  PATCH /admin/config/plans/{plan}. */
const PlanCard = ({ plan, def, onSave }) => {
  const [dailyCr, setDailyCr] = useState(def.daily_credits ?? def.daily_credit_limit ?? 0);
  const [weeklyCr, setWeeklyCr] = useState(def.weekly_credits ?? def.weekly_credit_limit ?? 0);
  const [dailyTok, setDailyTok] = useState(((def.daily_tokens ?? def.daily_token_limit) || 0) / 1e6);
  const [weeklyTok, setWeeklyTok] = useState(((def.weekly_tokens ?? def.weekly_token_limit) || 0) / 1e6);
  const [models, setModels] = useState(new Set(def.models || []));
  const [saved, setSaved] = useState(false);

  const toggleModel = (m) => {
    const s = new Set(models);
    s.has(m) ? s.delete(m) : s.add(m);
    setModels(s);
    setSaved(false);
  };

  const save = () => {
    onSave(plan, {
      daily_credits: dailyCr === '' ? null : Math.round(Number(dailyCr)),
      weekly_credits: weeklyCr === '' ? null : Math.round(Number(weeklyCr)),
      daily_tokens: Math.round(dailyTok * 1e6) || null,
      weekly_tokens: Math.round(weeklyTok * 1e6) || null,
      models: [...models],
    });
    setSaved(true);
  };

  return (
    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white capitalize flex items-center gap-2">
          {plan}
          {def._overridden && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">override</span>}
        </h3>
        <button onClick={save}
          className="px-3 py-1 rounded-lg bg-cyan-600/25 border border-cyan-500/40 text-xs text-cyan-200 hover:bg-cyan-600/40">
          {saved ? 'Saved ✓' : 'Save'}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-slate-400">
          Daily limit (credits) <span className="text-[10px] text-slate-500">1cre=$0.01</span>
          <input type="number" min="-1" step="1" value={dailyCr}
            onChange={e => { setDailyCr(e.target.value); setSaved(false); }}
            className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200 outline-none focus:border-cyan-500/50" />
        </label>
        <label className="text-xs text-slate-400">
          Weekly limit (credits)
          <input type="number" min="-1" step="1" value={weeklyCr}
            onChange={e => { setWeeklyCr(e.target.value); setSaved(false); }}
            className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200 outline-none focus:border-cyan-500/50" />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-slate-500">
          Runaway backstop — daily (M tokens)
          <input type="number" min="0" step="1" value={dailyTok}
            onChange={e => { setDailyTok(Number(e.target.value)); setSaved(false); }}
            className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-400 outline-none focus:border-cyan-500/50" />
        </label>
        <label className="text-xs text-slate-500">
          Runaway backstop — weekly (M tokens)
          <input type="number" min="0" step="1" value={weeklyTok}
            onChange={e => { setWeeklyTok(Number(e.target.value)); setSaved(false); }}
            className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-400 outline-none focus:border-cyan-500/50" />
        </label>
      </div>
      <div>
        <div className="text-xs text-slate-400 mb-2">Allowed models</div>
        <div className="flex flex-wrap gap-1.5">
          {KNOWN_MODELS.map(m => (
            <button key={m} onClick={() => toggleModel(m)}
              className={`px-2 py-1 rounded-md text-[11px] border transition-colors ${
                models.has(m)
                  ? 'bg-cyan-600/20 border-cyan-500/40 text-cyan-300'
                  : 'bg-slate-900/40 border-slate-700 text-slate-500 hover:text-slate-300'}`}>
              {m}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Tiny chart primitives (no chart lib needed) ─────────────────── */

/** Single horizontal bar row: label | bar | value */
const HBar = ({ label, value, max, color, sub }) => (
  <div className="flex items-center gap-3">
    <div className="w-36 truncate text-[11px] text-slate-400 text-right" title={label}>{label}</div>
    <div className="flex-1 h-3 bg-slate-800/60 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${max > 0 ? Math.min(100, (value / max) * 100) : 0}%`, background: color }}
      />
    </div>
    <div className="w-20 text-[11px] text-slate-300 tabular-nums">{sub ?? value}</div>
  </div>
);

/** Stacked single-row bar with legend: parts=[{label,value,color}] */
const StackedBar = ({ parts }) => {
  const total = parts.reduce((s, p) => s + p.value, 0);
  return (
    <div>
      <div className="flex h-4 rounded-full overflow-hidden bg-slate-800/60">
        {parts.filter(p => p.value > 0).map((p) => (
          <div key={p.label} style={{ width: `${total > 0 ? (p.value / total) * 100 : 0}%`, background: p.color }} title={`${p.label}: ${p.value}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
        {parts.map((p) => (
          <span key={p.label} className="flex items-center gap-1.5 text-[10px] text-slate-400">
            <span className="w-2 h-2 rounded-sm inline-block" style={{ background: p.color }} />
            {p.label} <span className="text-slate-500 tabular-nums">{formatTokens(p.value)}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// Sidebar section ids — synced to the URL hash so a refresh (or a shared
// link like /admin#logs) lands on the same section instead of Overview.
const ADMIN_TAB_IDS = new Set([
  'stats', 'usage', 'quality', 'models', 'costs', 'adoption', 'ops', 'logs',
  'revenue', 'alerts', 'config', 'broadcast', 'coupons', 'users', 'audit', 'contacts',
]);

const tabFromHash = () => {
  const seg = window.location.hash.replace(/^#\/?/, '').split('/');
  return {
    tab: ADMIN_TAB_IDS.has(seg[0]) ? seg[0] : 'stats',
    // '#users/<id>' — an open user detail survives refresh/back-forward
    userId: seg[0] === 'users' && seg[1] ? decodeURIComponent(seg[1]) : null,
  };
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem('admin_key') || '');
  const [sessionToken, setSessionToken] = useState(() => sessionStorage.getItem('admin_session') || '');
  const [authed, setAuthed] = useState(() => !!(sessionStorage.getItem('admin_key') || sessionStorage.getItem('admin_session')));
  const [adminEmail, setAdminEmail] = useState(() => sessionStorage.getItem('admin_email') || '');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [useKeyFallback, setUseKeyFallback] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [userSort, setUserSort] = useState('created');
  const [planFilter, setPlanFilter] = useState('');
  const [blockedFilter, setBlockedFilter] = useState('');
  const [userQuota, setUserQuota] = useState(null);
  const [activeTab, setActiveTab] = useState(() => tabFromHash().tab);
  const [pendingUserId, setPendingUserId] = useState(() => tabFromHash().userId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [stats, setStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCoupon, setNewCoupon] = useState({ code: '', plan: 'pro', max_uses: -1, expires_at: '' });
  const [copiedCode, setCopiedCode] = useState(null);

  const [usersList, setUsersList] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPlan, setEditingPlan] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userUsage, setUserUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  // Daily Usage tab — calendar-driven per-user token/cost report
  const [usageDate, setUsageDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [usageAllTime, setUsageAllTime] = useState(false);
  const [dailyUsage, setDailyUsage] = useState(null);
  const [dailyUsageLoading, setDailyUsageLoading] = useState(false);
  const [usageDays, setUsageDays] = useState(30);
  // Usage Quality tab — per-user efficiency/waste signals over N days
  const [qualityDays, setQualityDays] = useState(7);
  const [qualityData, setQualityData] = useState(null);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [qualitySearch, setQualitySearch] = useState('');
  const [qualitySort, setQualitySort] = useState('verdict');
  // Analytics tabs (models / costs / adoption / alerts)
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [modelsData, setModelsData] = useState(null);
  const [conversationsData, setConversationsData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [costsData, setCostsData] = useState(null);
  const [featuresData, setFeaturesData] = useState(null);
  const [versionsData, setVersionsData] = useState(null);
  const [quotaPressure, setQuotaPressure] = useState(null);
  const [alertsData, setAlertsData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  // Round-2 tabs: Ops (live/runaway/errors), Revenue, Config, Broadcast, User360
  const [liveFeed, setLiveFeed] = useState(null);
  const [runawayData, setRunawayData] = useState(null);
  const [errorsData, setErrorsData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [planConfig, setPlanConfig] = useState(null);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastSev, setBroadcastSev] = useState('info');
  const [broadcastHours, setBroadcastHours] = useState(72);
  const [banners, setBanners] = useState([]);
  const [user360, setUser360] = useState(null);
  const USERS_PER_PAGE = 50;

  // CREDIT quota override editor — values in credits (1 cre = $0.01 API cost).
  // '' = plan default, 'unlimited' checkbox = -1, number = per-user override.
  const [quotaDraft, setQuotaDraft] = useState({ daily: '', weekly: '', dailyUnlimited: false, weeklyUnlimited: false });
  const [savingQuota, setSavingQuota] = useState(false);
  const [quotaSaved, setQuotaSaved] = useState('');

  // Plan credit defaults (must mirror backend PLAN_DEFINITIONS)
  const PLAN_CREDIT_DEFAULTS = {
    free: { d: 35, w: 100 },
    pro: { d: 200, w: 800 },
    team: { d: -1, w: -1 },
    enterprise: { d: -1, w: -1 },
  };

  // Contact submissions state
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  // Admin auth: session token (preferred — tied to a real admin account,
  // auditable) OR legacy env key. Both headers sent; backend accepts either.
  const adminHeaders = {
    'Content-Type': 'application/json',
    ...(sessionToken ? { 'X-Session-Token': sessionToken } : {}),
    ...(adminKey ? { 'X-Admin-Key': adminKey } : {}),
  };

  const apiCall = async (endpoint, options = {}) => {
    const url = `${API_BASE}/api${endpoint}`;
    const res = await fetch(url, {
      ...options,
      headers: { ...adminHeaders, ...(options.headers || {}) },
    });
    if (res.status === 403) {
      setAuthed(false);
      sessionStorage.removeItem('admin_key');
      throw new Error('Invalid admin key');
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(data.detail || `HTTP ${res.status}`);
    }
    return res.json();
  };

  // Primary login: admin's own email/password → session token → /admin/me gate.
  // Fallback: legacy admin key (kept for emergencies until rotated out).
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (!useKeyFallback) {
        if (!loginEmail.trim() || !loginPassword) { setError('Enter email and password'); setLoading(false); return; }
        const res = await fetch(`${API_BASE}/api/auth/email/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: loginEmail.trim(), password: loginPassword }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || 'Login failed');
        const token = data.session_token;
        // Verify this account is actually an admin before unlocking the panel
        const me = await fetch(`${API_BASE}/api/admin/me`, {
          headers: { 'X-Session-Token': token },
        }).then(r => r.json());
        if (!me.is_admin) {
          throw new Error(`Account ${loginEmail} is not an admin. Ask an existing admin to grant access.`);
        }
        setSessionToken(token);
        setAdminEmail(me.email);
        sessionStorage.setItem('admin_session', token);
        sessionStorage.setItem('admin_email', me.email);
        sessionStorage.removeItem('admin_key');
        setAdminKey('');
      } else {
        if (!adminKey.trim()) { setError('Enter admin key'); setLoading(false); return; }
        const res = await fetch(`${API_BASE}/api/admin/stats`, { headers: { 'X-Admin-Key': adminKey } });
        if (!res.ok) throw new Error('Invalid admin key');
        sessionStorage.setItem('admin_key', adminKey);
        sessionStorage.removeItem('admin_session');
        sessionStorage.removeItem('admin_email');
        setSessionToken('');
        setAdminEmail('');
      }
      setAuthed(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_key');
    sessionStorage.removeItem('admin_session');
    sessionStorage.removeItem('admin_email');
    setAuthed(false);
    setAdminKey('');
    setSessionToken('');
    setAdminEmail('');
    setStats(null);
    setCoupons([]);
    setUsersList([]);
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall('/admin/stats');
      setStats(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [adminKey]);

  // Overview bundle: stats + today's totals + 7d series + pulse metrics
  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const [st, daily, costs, models, errs, run, alerts] = await Promise.allSettled([
        apiCall('/admin/stats'),
        apiCall(`/admin/usage/daily?date=${today}`),
        apiCall('/admin/usage/costs?days=7'),
        apiCall('/admin/usage/models?days=1'),
        apiCall('/admin/errors?days=1'),
        apiCall('/admin/usage/runaway?days=7'),
        apiCall('/admin/alerts'),
      ]);
      const val = r => (r.status === 'fulfilled' ? r.value : null);
      const statsVal = val(st);
      if (statsVal) setStats(statsVal);
      setOverview({ daily: val(daily), costs: val(costs), models: val(models), errors: val(errs), runaway: val(run), alerts: val(alerts) });
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [adminKey, sessionToken]);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall('/coupons/list');   // auth via headers — never in URL
      setCoupons(data.coupons || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [adminKey, sessionToken]);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall('/admin/audit');
      setAuditLog(data.entries || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [adminKey, sessionToken]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: USERS_PER_PAGE, skip: usersPage * USERS_PER_PAGE, sort: userSort });
      if (searchQuery) params.set('search', searchQuery);
      if (planFilter) params.set('plan', planFilter);
      if (blockedFilter) params.set('blocked', blockedFilter);
      const data = await apiCall(`/admin/users?${params}`);
      setUsersList(data.users || []);
      setUsersTotal(data.total || 0);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [adminKey, sessionToken, usersPage, searchQuery, userSort, planFilter, blockedFilter]);

  const fetchDailyUsage = useCallback(async () => {
    setDailyUsageLoading(true);
    try {
      const data = await apiCall(`/admin/usage/daily?date=${usageAllTime ? 'all' : usageDate}`);
      setDailyUsage(data);
    } catch (err) { setError(err.message); }
    finally { setDailyUsageLoading(false); }
  }, [adminKey, sessionToken, usageDate, usageAllTime]);

  // ── Analytics fetches ────────────────────────────────────────────────
  const fetchModels = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const [m, c, h] = await Promise.all([
        apiCall(`/admin/usage/models?days=${analyticsDays}`),
        apiCall(`/admin/usage/conversations?days=${analyticsDays}`),
        apiCall(`/admin/usage/health?days=${analyticsDays}`),
      ]);
      setModelsData(m); setConversationsData(c); setHealthData(h);
    } catch (err) { setError(err.message); }
    finally { setAnalyticsLoading(false); }
  }, [adminKey, sessionToken, analyticsDays]);

  const fetchCosts = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      setCostsData(await apiCall(`/admin/usage/costs?days=${analyticsDays}`));
    } catch (err) { setError(err.message); }
    finally { setAnalyticsLoading(false); }
  }, [adminKey, sessionToken, analyticsDays]);

  const fetchAdoption = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const [f, v, q] = await Promise.all([
        apiCall(`/admin/usage/features?days=${analyticsDays}`),
        apiCall(`/admin/versions?days=${analyticsDays}`),
        apiCall(`/admin/usage/quota-pressure`),
      ]);
      setFeaturesData(f); setVersionsData(v); setQuotaPressure(q);
    } catch (err) { setError(err.message); }
    finally { setAnalyticsLoading(false); }
  }, [adminKey, sessionToken, analyticsDays]);

  const fetchAlerts = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const [a, q] = await Promise.all([
        apiCall(`/admin/alerts`),
        apiCall(`/admin/usage/quota-pressure`),
      ]);
      setAlertsData(a); setQuotaPressure(q);
    } catch (err) { setError(err.message); }
    finally { setAnalyticsLoading(false); }
  }, [adminKey, sessionToken]);

  // CSV download — fetch blob with admin headers, save via object URL
  const downloadCsv = async (endpoint, filename) => {
    try {
      const res = await fetch(`${API_BASE}/api${endpoint}`, { headers: adminHeaders });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); }
  };

  // ── Round-2 fetches ──────────────────────────────────────────────────
  const fetchOps = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const [l, r, e] = await Promise.all([
        apiCall('/admin/usage/live?limit=80'),
        apiCall(`/admin/usage/runaway?days=${analyticsDays}`),
        apiCall(`/admin/errors?days=${analyticsDays}`),
      ]);
      setLiveFeed(l); setRunawayData(r); setErrorsData(e);
    } catch (err) { setError(err.message); }
    finally { setAnalyticsLoading(false); }
  }, [adminKey, sessionToken, analyticsDays]);

  const fetchRevenue = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      setRevenueData(await apiCall('/admin/revenue?days=90'));
    } catch (err) { setError(err.message); }
    finally { setAnalyticsLoading(false); }
  }, [adminKey, sessionToken]);

  const fetchConfig = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      setPlanConfig(await apiCall('/admin/config/plans'));
    } catch (err) { setError(err.message); }
    finally { setAnalyticsLoading(false); }
  }, [adminKey, sessionToken]);

  const savePlanOverride = async (plan, body) => {
    try {
      await apiCall(`/admin/config/plans/${plan}`, { method: 'PATCH', body: JSON.stringify(body) });
      fetchConfig();
    } catch (err) { setError(err.message); }
  };

  const fetchBanners = useCallback(async () => {
    try {
      const d = await apiCall('/admin/broadcast');
      setBanners(d.banners || []);
    } catch (err) { setError(err.message); }
  }, [adminKey, sessionToken]);

  const postBroadcast = async () => {
    if (!broadcastMsg.trim()) { setError('Enter a banner message'); return; }
    try {
      await apiCall('/admin/broadcast', {
        method: 'POST',
        body: JSON.stringify({ message: broadcastMsg.trim(), severity: broadcastSev, expires_hours: broadcastHours }),
      });
      setBroadcastMsg('');
      fetchBanners();
    } catch (err) { setError(err.message); }
  };

  const clearBroadcast = async () => {
    try {
      await apiCall('/admin/broadcast', { method: 'DELETE' });
      fetchBanners();
    } catch (err) { setError(err.message); }
  };

  const fetchQuality = useCallback(async () => {
    setQualityLoading(true);
    try {
      const data = await apiCall(`/admin/usage/quality?days=${qualityDays}`);
      setQualityData(data);
    } catch (err) { setError(err.message); }
    finally { setQualityLoading(false); }
  }, [adminKey, sessionToken, qualityDays]);

  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const data = await apiCall('/contact/submissions');
      setContacts(data.submissions || []);
    } catch (err) { setError(err.message); }
    finally { setContactsLoading(false); }
  }, [adminKey]);

  // Write the active section into the URL hash — '#tab' for sections,
  // '#users/<id>' while a user detail is open — so refresh keeps the view
  // and back/forward navigates between sections and the open user.
  useEffect(() => {
    const target = activeTab === 'users' && selectedUser
      ? `users/${encodeURIComponent(selectedUser.id)}`
      : activeTab;
    if (window.location.hash.replace(/^#/, '') !== target) {
      window.location.hash = target;
    }
  }, [activeTab, selectedUser]);

  // Back/forward buttons → hashchange → restore the matching section (and
  // reopen/close the user detail to match the hash).
  useEffect(() => {
    const onHash = () => {
      const { tab, userId } = tabFromHash();
      setActiveTab(tab);
      if (userId) {
        setPendingUserId(userId);
      } else {
        setPendingUserId(null);
        setSelectedUser(null);
        setUserUsage(null);
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Restore an open user detail from the URL hash — on refresh the user
  // object is gone, so refetch it via the single-user endpoint (the users
  // list is paginated and can't be relied on).
  useEffect(() => {
    if (!authed || !pendingUserId || activeTab !== 'users') return;
    if (selectedUser?.id === pendingUserId) { setPendingUserId(null); return; }
    const id = pendingUserId;
    setPendingUserId(null);
    apiCall(`/admin/users/${id}`)
      .then(u => { if (u?.id) openUserDetail(u); })
      .catch(() => setSelectedUser(null));
  }, [authed, pendingUserId, activeTab, selectedUser]);

  useEffect(() => {
    if (!authed) return;
    setError('');
    if (activeTab === 'stats') fetchOverview();
    else if (activeTab === 'coupons') fetchCoupons();
    else if (activeTab === 'contacts') fetchContacts();
    else if (activeTab === 'audit') fetchAudit();
    else if (activeTab === 'usage') fetchDailyUsage();
    else if (activeTab === 'quality') fetchQuality();
    else if (activeTab === 'models') fetchModels();
    else if (activeTab === 'costs') fetchCosts();
    else if (activeTab === 'adoption') fetchAdoption();
    else if (activeTab === 'alerts') fetchAlerts();
    else if (activeTab === 'ops') fetchOps();
    else if (activeTab === 'revenue') fetchRevenue();
    else if (activeTab === 'config') fetchConfig();
    else if (activeTab === 'broadcast') fetchBanners();
    else if (activeTab === 'users') fetchStats();
    // 'users' handled by the dedicated effect below — it must also react to
    // filter/sort/page changes, not just the tab switch.
  }, [authed, activeTab, fetchDailyUsage, fetchQuality, fetchModels, fetchCosts, fetchAdoption, fetchAlerts, fetchOps, fetchRevenue, fetchConfig, fetchBanners, fetchOverview]);

  // Live feed auto-refresh while the Ops tab is open
  useEffect(() => {
    if (!authed || activeTab !== 'ops') return;
    const t = setInterval(() => {
      apiCall('/admin/usage/live?limit=80').then(setLiveFeed).catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, [authed, activeTab]);

  // User 360 — fetched when a user detail opens
  useEffect(() => {
    if (!authed || !selectedUser?.id) { setUser360(null); return; }
    apiCall(`/admin/users/${selectedUser.id}/360`).then(setUser360).catch(() => setUser360(null));
  }, [authed, selectedUser?.id]);

  // Quality tab: refetch when the day window changes
  useEffect(() => {
    if (authed && activeTab === 'quality') fetchQuality();
  }, [qualityDays]);

  // Users list: refetch on search (debounced), filters, sort, page.
  useEffect(() => {
    if (!authed || activeTab !== 'users') return;
    const t = setTimeout(() => fetchUsers(), searchQuery ? 350 : 0);
    return () => clearTimeout(t);
  }, [authed, activeTab, fetchUsers]);

  const createCoupon = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const body = { ...newCoupon, admin_key: adminKey };
      if (!body.expires_at) delete body.expires_at;
      await apiCall('/coupons/create', { method: 'POST', body: JSON.stringify(body) });
      setShowCreateForm(false);
      setNewCoupon({ code: '', plan: 'pro', max_uses: -1, expires_at: '' });
      fetchCoupons();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const toggleCoupon = async (code, currentActive) => {
    const action = currentActive ? 'deactivate' : 'activate';
    try {
      await apiCall(`/admin/coupons/${code}/${action}`, { method: 'POST' });
      fetchCoupons();
    } catch (err) { setError(err.message); }
  };

  const deleteCoupon = async (code) => {
    if (!confirm(`Delete coupon "${code}" permanently?`)) return;
    try {
      await apiCall(`/admin/coupons/${code}`, { method: 'DELETE' });
      fetchCoupons();
    } catch (err) { setError(err.message); }
  };

  const updateUserPlan = async (userId, newPlan) => {
    try {
      await apiCall(`/admin/users/${userId}/plan`, { method: 'PATCH', body: JSON.stringify({ plan: newPlan }) });
      setEditingPlan(null);
      fetchUsers();
    } catch (err) { setError(err.message); }
  };

  const extendUserPlan = async (userId, email) => {
    try {
      const r = await apiCall(`/admin/users/${userId}/extend`, { method: 'POST', body: JSON.stringify({ days: 30 }) });
      setQuotaSaved(`Extended ${r.plan} +30d — expires ${new Date(r.renews_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
      setTimeout(() => setQuotaSaved(''), 4000);
      fetchUsers();
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser(u => u ? { ...u, plan: r.plan, plan_expired: false, renews_at: r.renews_at, plan_expires_at: r.renews_at, days_left: r.days, manual_grant: false } : u);
      }
    } catch (err) { setError(err.message); }
  };

  const deleteUser = async (userId, userName) => {
    if (!confirm(`Delete user "${userName}" permanently? This will remove all their data, sessions, and usage history.`)) return;
    try {
      await apiCall(`/admin/users/${userId}`, { method: 'DELETE' });
      fetchUsers();
    } catch (err) { setError(err.message); }
  };

  const saveUserQuota = async (userId) => {
    setSavingQuota(true);
    setQuotaSaved('');
    const toCredits = (v, unlimited) =>
      unlimited ? -1 : (v === '' || v == null ? null : Math.max(1, Math.round(parseFloat(v))));
    try {
      await apiCall(`/admin/users/${userId}/quota`, {
        method: 'PATCH',
        body: JSON.stringify({
          daily: toCredits(quotaDraft.daily, quotaDraft.dailyUnlimited),
          weekly: toCredits(quotaDraft.weekly, quotaDraft.weeklyUnlimited),
        }),
      });
      // Reflect the override on the open user object so the panel stays in sync
      setSelectedUser(u => u ? {
        ...u,
        quota_daily_override: toCredits(quotaDraft.daily, quotaDraft.dailyUnlimited),
        quota_weekly_override: toCredits(quotaDraft.weekly, quotaDraft.weeklyUnlimited),
      } : u);
      setQuotaSaved('Saved — effective immediately');
      fetchUsers();
      setTimeout(() => setQuotaSaved(''), 3000);
    } catch (err) { setError(err.message); }
    finally { setSavingQuota(false); }
  };

  const suspendUser = async (userId, email, suspend) => {
    const verb = suspend ? 'suspend' : 'unsuspend';
    if (suspend && !confirm(`Suspend "${email}"? They'll be logged out and can't sign in or use AI until unsuspended.`)) return;
    try {
      await apiCall(`/admin/users/${userId}/${suspend ? 'suspend' : 'unsuspend'}`, { method: 'POST' });
      fetchUsers();
    } catch (err) { setError(err.message); }
  };

  const forceLogoutUser = async (userId, email) => {
    if (!confirm(`Force-logout "${email}"? All sessions and IDE tokens are killed immediately.`)) return;
    try {
      const r = await apiCall(`/admin/users/${userId}/logout`, { method: 'POST' });
      setQuotaSaved(`Logged out (${r.sessions_removed + r.ide_tokens_removed} tokens)`);
      setTimeout(() => setQuotaSaved(''), 3000);
    } catch (err) { setError(err.message); }
  };

  const setUserRole = async (userId, email, makeAdmin) => {
    const msg = makeAdmin ? `Grant admin to "${email}"? Full dashboard access.` : `Remove admin from "${email}"?`;
    if (!confirm(msg)) return;
    try {
      await apiCall(`/admin/users/${userId}/role`, { method: 'PATCH', body: JSON.stringify({ role: makeAdmin ? 'admin' : 'user' }) });
      fetchUsers();
    } catch (err) { setError(err.message); }
  };

  const openUserDetail = async (user) => {
    setSelectedUser(user);
    setUserQuota(null);
    apiCall(`/admin/users/${user.id}/quota`).then(setUserQuota).catch(() => setUserQuota(null));
    setQuotaDraft({
      daily: user.quota_daily_override != null && user.quota_daily_override !== -1 ? String(user.quota_daily_override) : '',
      weekly: user.quota_weekly_override != null && user.quota_weekly_override !== -1 ? String(user.quota_weekly_override) : '',
      dailyUnlimited: user.quota_daily_override === -1,
      weeklyUnlimited: user.quota_weekly_override === -1,
    });
    setQuotaSaved('');
    setUserUsage(null);
    setUsageLoading(true);
    try {
      const data = await apiCall(`/admin/users/${user.id}/usage?days=${usageDays}`);
      setUserUsage(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setUsageLoading(false);
    }
  };

  const closeUserDetail = () => {
    setSelectedUser(null);
    setUserUsage(null);
  };

  const changeUsageDays = async (days) => {
    setUsageDays(days);
    if (!selectedUser) return;
    setUsageLoading(true);
    setUserUsage(null);
    try {
      const data = await apiCall(`/admin/users/${selectedUser.id}/usage?days=${days}`);
      setUserUsage(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setUsageLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // ── Login screen ──
  if (!authed) {
    return (
      <div className="hero-ref-bg text-slate-100 min-h-screen flex items-center justify-center relative page-transition">
        <div className="absolute top-10 left-1/4 w-[500px] h-[300px] bg-blue-600/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute top-20 right-10 w-[600px] h-[400px] bg-cyan-500/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="relative z-10 w-full max-w-md mx-4">
          <div className="dl-card rounded-3xl p-8">
            <div className="flex flex-col items-center gap-3 mb-6">
              <img src="/elinnos-logo.png" alt="Elinnos" className="h-14 w-auto" />
              <div className="text-center">
                <h1 className="text-xl font-bold text-white">Admin Panel</h1>
                <p className="text-sm text-slate-400">chipdesigner.ai</p>
              </div>
            </div>
            <form onSubmit={handleLogin}>
              {!useKeyFallback ? (
                <>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Admin Email</label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition-colors mb-3"
                    autoFocus
                  />
                  <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Your account password"
                    className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                  />
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Admin Key <span className="text-slate-500 font-normal">(legacy — being phased out)</span></label>
                  <input
                    type="password"
                    value={adminKey}
                    onChange={(e) => setAdminKey(e.target.value)}
                    placeholder="Enter admin key"
                    className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                    autoFocus
                  />
                </>
              )}
              {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
              <button
                type="submit"
                disabled={loading || (useKeyFallback ? !adminKey.trim() : (!loginEmail.trim() || !loginPassword))}
                className="w-full mt-4 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-semibold px-6 py-3 rounded-xl transition-all disabled:opacity-50"
              >
                {loading ? 'Verifying...' : useKeyFallback ? 'Access with Key' : 'Sign in as Admin'}
              </button>
              <button
                type="button"
                onClick={() => { setUseKeyFallback(v => !v); setError(''); }}
                className="w-full mt-3 text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                {useKeyFallback ? '← Back to account login' : 'Use legacy admin key instead'}
              </button>
            </form>
            <button
              onClick={() => navigate('/')}
              className="w-full mt-3 text-sm text-slate-400 hover:text-white transition-colors"
            >
              ← Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Usage Quality: client-side search + sort on the fetched window
  const qualityRows = (() => {
    if (!qualityData) return [];
    const q = qualitySearch.trim().toLowerCase();
    let rows = qualityData.users.filter(u =>
      !q ||
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.user || '').toLowerCase().includes(q));
    const rank = { efficient: 0, good: 1, mixed: 2, wasteful: 3, suspicious: 4, unrated: 5 };
    switch (qualitySort) {
      case 'prompting':
        rows = [...rows].sort((a, b) => (b.prompt_score) - (a.prompt_score));
        break;
      case 'cost':
        rows = [...rows].sort((a, b) => b.est_cost_usd - a.est_cost_usd);
        break;
      case 'tokens':
        rows = [...rows].sort((a, b) => (b.input_tokens + b.output_tokens) - (a.input_tokens + a.output_tokens));
        break;
      case 'conversations':
        rows = [...rows].sort((a, b) => b.conversations - a.conversations);
        break;
      case 'completion':
        rows = [...rows].sort((a, b) => b.completion_pct - a.completion_pct);
        break;
      default: // 'verdict' — best rating first, score desc inside
        rows = [...rows].sort((a, b) => (rank[a.rating] ?? 9) - (rank[b.rating] ?? 9) || b.score - a.score);
    }
    return rows;
  })();

  const navItems = [
    { id: 'stats', label: 'Overview', icon: BarChart3 },
    { id: 'usage', label: 'Daily Usage', icon: Coins },
    { id: 'quality', label: 'Usage Quality', icon: Activity },
    { id: 'models', label: 'Models', icon: Cpu },
    { id: 'costs', label: 'Costs', icon: DollarSign },
    { id: 'adoption', label: 'Adoption', icon: Monitor },
    { id: 'ops', label: 'Ops', icon: Gauge },
    { id: 'logs', label: 'Live Logs', icon: Terminal },
    { id: 'revenue', label: 'Revenue', icon: TrendingUp },
    { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
    { id: 'config', label: 'Config', icon: Settings },
    { id: 'broadcast', label: 'Broadcast', icon: Radio },
    { id: 'coupons', label: 'Coupons', icon: Ticket },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'audit', label: 'Audit Log', icon: Shield },
    { id: 'contacts', label: 'Contacts', icon: Mail },
  ];

  return (
    <div className="hero-ref-bg text-slate-100 min-h-screen relative page-transition flex">
      <div className="absolute top-10 left-1/4 w-[500px] h-[300px] bg-blue-600/10 blur-[100px] rounded-full pointer-events-none" />
      <div className="absolute top-20 right-10 w-[600px] h-[400px] bg-cyan-500/10 blur-[120px] rounded-full pointer-events-none" />

      {/* ── Sidebar ── */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 z-50 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${sidebarCollapsed ? 'lg:-translate-x-full' : 'lg:translate-x-0'}`}
        style={{ background: 'rgba(10, 14, 26, 0.97)', backdropFilter: 'blur(16px)' }}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-800/60 shrink-0">
            <img src="/elinnos-logo.png" alt="Elinnos" className="h-9 w-auto" />
            <span className="text-slate-600 font-light">|</span>
            <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              Admin
            </span>
            <button
              onClick={() => setSidebarCollapsed(true)}
              title="Hide sidebar"
              className="ml-auto hidden lg:flex items-center justify-center w-7 h-7 rounded-lg text-slate-500 hover:text-cyan-400 hover:bg-slate-800/60 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>

          <nav className="flex-1 min-h-0 px-3 py-4 space-y-1 overflow-y-auto admin-scroll"
            style={{ WebkitOverflowScrolling: 'touch' }}>
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  activeTab === item.id
                    ? 'bg-gradient-to-r from-blue-600/20 to-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/40'
                }`}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                {item.label}
              </button>
            ))}
          </nav>

          <div className="px-3 py-4 border-t border-slate-800/60 shrink-0">
            {adminEmail && (
              <div className="px-4 py-2 mb-2 text-xs text-slate-500 truncate" title={adminEmail}>
                Signed in as <span className="text-slate-300">{adminEmail}</span>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-4 h-4 shrink-0" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Desktop expand handle — floats at the top-left when sidebar hidden */}
      {sidebarCollapsed && (
        <button
          onClick={() => setSidebarCollapsed(false)}
          title="Show sidebar"
          className="hidden lg:flex fixed top-4 left-4 z-50 items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-slate-300 hover:text-cyan-400 transition-colors"
          style={{ background: 'rgba(10, 14, 26, 0.95)', border: '1px solid rgba(51, 65, 85, 0.6)', backdropFilter: 'blur(12px)' }}
        >
          <ChevronRight className="w-4 h-4" />
          Menu
        </button>
      )}

      {/* ── Main content ── */}
      <div className={`flex-1 min-w-0 relative z-10 flex flex-col min-h-screen transition-[margin] duration-300 ${sidebarCollapsed ? 'lg:ml-0' : 'lg:ml-64'}`}>
        <header className="lg:hidden flex items-center justify-between h-14 px-4 border-b border-slate-800/60 bg-[#0a0e1a]/80 backdrop-blur-xl sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} className="text-slate-300 hover:text-white">
            <Menu className="w-6 h-6" />
          </button>
          <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
            Admin · {navItems.find(n => n.id === activeTab)?.label}
          </span>
          <div className="w-6" />
        </header>

        <div className="flex-1 min-w-0 w-full max-w-full p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          {error && (
            <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError('')} className="text-red-300 hover:text-red-200 ml-3">✕</button>
            </div>
          )}

          {/* ════════ Overview ════════ */}
          {activeTab === 'stats' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-white">System Overview</h2>
                  <p className="text-sm text-slate-400 mt-1">Today at a glance — usage, cost, activity &amp; health</p>
                </div>
                <button onClick={fetchOverview} className="px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-cyan-500/40">Refresh</button>
              </div>

              {loading ? (
                <div className="flex items-center gap-3 text-slate-400">
                  <div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
                  Loading...
                </div>
              ) : stats ? (
                <>
                  {/* Alerts strip — surface problems on the landing tab */}
                  {(overview?.alerts?.alerts || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {overview.alerts.alerts.slice(0, 4).map((a, i) => (
                        <button key={i} onClick={() => setActiveTab('alerts')}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs border transition-colors ${
                            a.severity === 'high' ? 'bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20'
                            : 'bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20'}`}>
                          <AlertTriangle className="w-3.5 h-3.5" />
                          <span className="capitalize font-medium">{a.kind.replace(/_/g, ' ')}</span>
                          <span className="opacity-70 max-w-[260px] truncate">{a.message}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Today — hero KPIs */}
                  {overview?.daily?.totals && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">Today (UTC)</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {[
                          { label: 'Requests', num: overview.daily.totals.requests, c: 'text-white' },
                          { label: 'Tokens', num: overview.daily.totals.tokens, c: 'text-white' },
                          { label: 'Est. cost', num: overview.daily.totals.est_cost_usd || 0, prefix: '$', decimals: 2, raw: true, c: 'text-cyan-300' },
                          { label: 'Active users', num: overview.daily.totals.users, c: 'text-white' },
                          { label: 'Cache hit', num: overview.daily.totals.cache_hit_pct || 0, decimals: 1, raw: true, suffix: '%', c: (overview.daily.totals.cache_hit_pct || 0) >= 80 ? 'text-green-400' : 'text-amber-400' },
                          { label: 'Errors', num: overview.errors?.total_errors ?? 0, c: (overview.errors?.total_errors || 0) > 0 ? 'text-red-400' : 'text-green-400' },
                        ].map(k => (
                          <div key={k.label} className="dl-card rounded-2xl p-4">
                            <p className="text-[11px] text-slate-400">{k.label}</p>
                            <p className={`text-xl font-bold mt-1 tabular-nums ${k.c}`}>
                              <CountUp value={k.num} prefix={k.prefix || ''} suffix={k.suffix || ''} decimals={k.decimals || 0} raw={k.raw} />
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 7-day activity chart + models today */}
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    <div className="dl-card rounded-2xl p-5 lg:col-span-3">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-white">Last 7 days — requests &amp; cost</h3>
                        {overview?.costs && (
                          <span className="text-xs text-cyan-300 font-semibold tabular-nums">
                            $<CountUp value={overview.costs.total_usd} decimals={2} raw /> total
                          </span>
                        )}
                      </div>
                      <AreaChart data={overview?.costs?.daily} />
                    </div>

                    <div className="dl-card rounded-2xl p-5 lg:col-span-2">
                      <h3 className="text-sm font-semibold text-white mb-4">Models today</h3>
                      <div className="space-y-3">
                        {(overview?.models?.models || []).slice(0, 5).map(m => {
                          const total = (overview?.models?.models || []).reduce((a, x) => a + x.requests, 0);
                          return (
                            <div key={m.model}>
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-cyan-300 font-medium">{m.model}</span>
                                <span className="text-slate-400">{m.requests} req · ${m.cost_usd.toFixed(2)}</span>
                              </div>
                              <div className="h-2 bg-slate-800/60 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-cyan-600 to-blue-500 rounded-full bar-grow"
                                  style={{ width: `${total ? (m.requests / total * 100) : 0}%` }} />
                              </div>
                            </div>
                          );
                        })}
                        {(!overview?.models?.models || overview.models.models.length === 0) && (
                          <p className="text-xs text-slate-500">No model usage today</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Pulse row — live traffic + flags + users */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="dl-card rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                        <p className="text-sm text-slate-400">Live traffic</p>
                      </div>
                      <p className="text-2xl font-bold text-white tabular-nums"><CountUp value={stats.traffic?.requests_last_minute || 0} /><span className="text-sm font-normal text-slate-500">/min</span></p>
                      <p className="text-[11px] text-slate-500 mt-1"><CountUp value={stats.traffic?.requests_last_hour || 0} /> in last hour</p>
                    </div>
                    <div className="dl-card rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-amber-400" /><p className="text-sm text-slate-400">Runaway convs (7d)</p></div>
                      <p className={`text-2xl font-bold tabular-nums ${(overview?.runaway?.count || 0) > 0 ? 'text-amber-400' : 'text-white'}`}><CountUp value={overview?.runaway?.count ?? 0} /></p>
                      <button onClick={() => setActiveTab('ops')} className="text-[11px] text-cyan-400 hover:underline mt-1">view in Ops →</button>
                    </div>
                    <div className="dl-card rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-blue-400" /><p className="text-sm text-slate-400">Users</p></div>
                      <p className="text-2xl font-bold text-white tabular-nums"><CountUp value={stats.users?.total || 0} /></p>
                      <p className="text-[11px] text-slate-500 mt-1">{stats.users?.pro || 0} pro · {stats.users?.recent_7d || 0} new (7d)</p>
                    </div>
                    <div className="dl-card rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-1"><Zap className="w-4 h-4 text-amber-400" /><p className="text-sm text-slate-400">Quota pressure</p></div>
                      <p className={`text-2xl font-bold tabular-nums ${(stats.users?.near_daily_cap || 0) > 0 ? 'text-amber-400' : 'text-white'}`}><CountUp value={stats.users?.near_daily_cap || 0} /></p>
                      <p className="text-[11px] text-slate-500 mt-1">users near daily cap</p>
                    </div>
                  </div>

                  {/* Plan distribution + sessions/coupons */}
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    <div className="dl-card rounded-2xl p-6 lg:col-span-3">
                      <h3 className="text-sm font-semibold text-white mb-4">Plan distribution</h3>
                      <div className="space-y-3.5">
                        {Object.entries(stats.plan_breakdown || {}).map(([plan, count]) => {
                          const pct = stats.users?.total ? Math.round((count / stats.users.total) * 100) : 0;
                          return (
                            <div key={plan}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-slate-300 capitalize font-medium">{plan}</span>
                                <span className="text-slate-400">{count} · {pct}%</span>
                              </div>
                              <div className="h-2 bg-slate-800/60 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all duration-500 ${plan === 'free' ? 'bg-slate-600' : 'bg-gradient-to-r from-blue-600 to-cyan-400'}`}
                                  style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                        {(!stats.plan_breakdown || Object.keys(stats.plan_breakdown).length === 0) && (
                          <p className="text-slate-500 text-sm">No plan data available</p>
                        )}
                      </div>
                    </div>
                    <div className="lg:col-span-2 grid grid-cols-1 gap-4">
                      <div className="dl-card rounded-2xl p-5 flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-400">Active sessions</p>
                          <p className="text-2xl font-bold text-white mt-1 tabular-nums"><CountUp value={stats.sessions?.total || 0} /></p>
                        </div>
                        <Activity className="w-6 h-6 text-blue-400/60" />
                      </div>
                      <div className="dl-card rounded-2xl p-5 flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-400">Coupons active</p>
                          <p className="text-2xl font-bold text-white mt-1 tabular-nums"><CountUp value={stats.coupons?.active || 0} /><span className="text-sm font-normal text-slate-500"> / {stats.coupons?.total || 0}</span></p>
                        </div>
                        <Ticket className="w-6 h-6 text-purple-400/60" />
                      </div>
                    </div>
                  </div>
                </>
              ) : <p className="text-slate-400">No data available</p>}
            </div>
          )}

          {/* ════════ Coupons ════════ */}
          {activeTab === 'coupons' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-white">Coupon Management</h2>
                  <p className="text-sm text-slate-400 mt-1">Create and manage promotional codes</p>
                </div>
                <button
                  onClick={() => setShowCreateForm(!showCreateForm)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
                >
                  <Plus className="w-4 h-4" /> Create Coupon
                </button>
              </div>

              {showCreateForm && (
                <div className="dl-card rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">New Coupon</h3>
                  <form onSubmit={createCoupon} className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-slate-300 mb-1.5">Code</label>
                      <input
                        type="text"
                        value={newCoupon.code}
                        onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                        placeholder="e.g. BETA-PRO-2026"
                        className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-300 mb-1.5">Plan</label>
                      <select
                        value={newCoupon.plan}
                        onChange={(e) => setNewCoupon({ ...newCoupon, plan: e.target.value })}
                        className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500/50"
                      >
                        <option value="pro">Pro</option>
                        <option value="team">Team</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-300 mb-1.5">Max Uses (-1 = unlimited)</label>
                      <input
                        type="number"
                        value={newCoupon.max_uses}
                        onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: parseInt(e.target.value) || -1 })}
                        className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-300 mb-1.5">Expiry (optional)</label>
                      <input
                        type="datetime-local"
                        value={newCoupon.expires_at}
                        onChange={(e) => setNewCoupon({ ...newCoupon, expires_at: e.target.value })}
                        className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500/50"
                      />
                    </div>
                    <div className="md:col-span-2 flex gap-3">
                      <button type="submit" disabled={loading} className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-6 py-2.5 rounded-xl text-sm font-medium disabled:opacity-50">
                        {loading ? 'Creating...' : 'Create'}
                      </button>
                      <button type="button" onClick={() => setShowCreateForm(false)} className="text-slate-400 hover:text-white px-6 py-2.5 rounded-xl text-sm">
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {loading ? (
                <div className="flex items-center gap-3 text-slate-400">
                  <div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
                  Loading...
                </div>
              ) : coupons.length === 0 ? (
                <div className="dl-card rounded-2xl p-12 text-center">
                  <Ticket className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No coupons yet. Create one to get started.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {coupons.map((c) => (
                    <div key={c.code} className="dl-card rounded-2xl p-5">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.active ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                            <Ticket className={`w-5 h-5 ${c.active ? 'text-green-400' : 'text-red-400'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-white font-mono">{c.code}</span>
                              <button onClick={() => copyToClipboard(c.code)} className="text-slate-400 hover:text-cyan-400 transition-colors">
                                {copiedCode === c.code ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                              </button>
                            </div>
                            <p className="text-sm text-slate-400">
                              Plan: <span className="text-cyan-400 capitalize">{c.plan}</span> ·
                              Uses: {c.used_count || 0}/{c.max_uses === -1 ? '∞' : c.max_uses}
                              {c.expires_at && ` · Expires: ${new Date(c.expires_at).toLocaleDateString()}`}
                            </p>
                            {c.redeemed_by && c.redeemed_by.length > 0 && (
                              <p className="text-xs text-slate-500 mt-1">
                                Redeemed by: {c.redeemed_by.map(r => r.email).join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleCoupon(c.code, c.active)}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${c.active ? 'text-amber-400 hover:bg-amber-500/10' : 'text-green-400 hover:bg-green-500/10'}`}
                          >
                            {c.active ? <><PowerOff className="w-4 h-4" /> Deactivate</> : <><Power className="w-4 h-4" /> Activate</>}
                          </button>
                          <button
                            onClick={() => deleteCoupon(c.code)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ════════ Users ════════ */}
          {/* ════════ Models (per-model analytics + health + turns/task) ════════ */}
          {activeTab === 'models' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-white">Model Analytics</h2>
                  <p className="text-sm text-slate-400 mt-1">Cost, cache, latency &amp; turns per task per model</p>
                </div>
                <div className="flex items-center gap-2">
                  <select value={analyticsDays} onChange={e => setAnalyticsDays(Number(e.target.value))}
                    className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200">
                    {[[1, 'Today'], [3, '3 days'], [7, '7 days'], [14, '14 days'], [30, '30 days'], [90, '90 days']]
                      .map(([d, l]) => <option key={d} value={d}>{l}</option>)}
                  </select>
                  <button onClick={fetchModels} className="px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-cyan-500/40">Refresh</button>
                  <button onClick={() => downloadCsv(`/admin/usage/models?days=${analyticsDays}&format=csv`, `models_${analyticsDays}d.csv`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600/20 border border-cyan-500/30 text-sm text-cyan-300 hover:bg-cyan-600/30">
                    <Download className="w-3.5 h-3.5" /> CSV
                  </button>
                </div>
              </div>

              {analyticsLoading ? (
                <div className="flex items-center gap-3 text-slate-400"><div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" /> Loading…</div>
              ) : modelsData ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Requests</div>
                      <div className="text-xl font-bold text-white mt-1 tabular-nums"><CountUp value={modelsData.totals.requests} /></div>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Est. cost</div>
                      <div className="text-xl font-bold text-cyan-300 mt-1 tabular-nums">$<CountUp value={modelsData.totals.cost_usd} decimals={2} raw /></div>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Models active</div>
                      <div className="text-xl font-bold text-white mt-1 tabular-nums"><CountUp value={modelsData.models.length} /></div>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Tracked convs</div>
                      <div className="text-xl font-bold text-white mt-1 tabular-nums">{conversationsData?.tracked_conversations != null ? <CountUp value={conversationsData.tracked_conversations} /> : '—'}</div>
                    </div>
                  </div>

                  <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-700/60">
                          <th className="px-3 py-2.5">Model</th><th className="px-3 py-2.5 text-right">Reqs</th>
                          <th className="px-3 py-2.5 text-right">Input</th><th className="px-3 py-2.5 text-right">Output</th>
                          <th className="px-3 py-2.5 text-right">Cache %</th><th className="px-3 py-2.5 text-right">Reasoning</th>
                          <th className="px-3 py-2.5 text-right">Cost</th><th className="px-3 py-2.5 text-right">Avg in/out</th>
                          <th className="px-3 py-2.5 text-right">Users</th><th className="px-3 py-2.5 text-right">Turns/conv</th>
                          <th className="px-3 py-2.5">Stops</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modelsData.models.map(m => (
                          <tr key={m.model} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                            <td className="px-3 py-2 font-medium text-cyan-300 whitespace-nowrap">{m.model}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{m.requests.toLocaleString()}</td>
                            <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatTokens(m.input_tokens)}</td>
                            <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatTokens(m.output_tokens)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{m.cache_hit_pct}%</td>
                            <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatTokens(m.reasoning_tokens)}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-cyan-300 whitespace-nowrap">${m.cost_usd.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatTokens(m.avg_input)}/{formatTokens(m.avg_output)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{m.users}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{m.avg_turns_per_conv ?? '—'}</td>
                            <td className="px-3 py-2 text-[11px] text-slate-400 whitespace-nowrap">
                              {Object.entries(m.stop_reasons).map(([k, v]) => `${k}:${v}`).join(' · ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Turns per task */}
                  {conversationsData && conversationsData.models.length > 0 && (
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-white mb-1">Turns per task (by conversation)</h3>
                      <p className="text-xs text-slate-500 mb-4">
                        {conversationsData.tracked_conversations} tracked conversations · {conversationsData.untracked_events} events predate tracking
                      </p>
                      <div className="space-y-2">
                        {conversationsData.models.map(m => (
                          <div key={m.model} className="flex items-center gap-4 text-sm">
                            <span className="w-32 text-cyan-300 truncate">{m.model}</span>
                            <span className="text-slate-400 w-24">{m.conversations} convs</span>
                            <div className="flex-1 flex items-center gap-3 text-xs text-slate-400">
                              <span>avg <b className="text-white">{m.avg_turns}</b></span>
                              <span>median <b className="text-white">{m.median_turns}</b></span>
                              <span>p90 <b className="text-amber-400">{m.p90_turns}</b></span>
                              <span>max <b className="text-red-400">{m.max_turns}</b></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Latency health */}
                  {healthData && (
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-white mb-3">Latency &amp; stop anomalies</h3>
                      <div className="space-y-2">
                        {healthData.models.map(h => (
                          <div key={h.model} className="flex items-center gap-4 text-sm flex-wrap">
                            <span className="w-32 text-cyan-300 truncate">{h.model}</span>
                            <span className="text-slate-400">avg <b className="text-white">{h.avg_ms}ms</b></span>
                            <span className="text-slate-400">p95 <b className="text-white">{h.p95_ms}ms</b></span>
                            <span className={h.other_stop_pct > 5 ? 'text-amber-400' : 'text-slate-400'}>
                              unusual stops <b>{h.other_stop_count}</b> ({h.other_stop_pct}%)
                            </span>
                          </div>
                        ))}
                        {healthData.models.every(h => !h.avg_ms) && (
                          <p className="text-xs text-slate-500">duration_ms not yet recorded — latency appears once events carry it.</p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : <p className="text-slate-500 text-sm">No data.</p>}
            </div>
          )}

          {/* ════════ Costs ════════ */}
          {activeTab === 'costs' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-white">Provider Spend</h2>
                  <p className="text-sm text-slate-400 mt-1">Estimated USD cost across all models ({analyticsDays === 1 ? 'today' : `${analyticsDays}-day window`})</p>
                </div>
                <div className="flex items-center gap-2">
                  <select value={analyticsDays} onChange={e => setAnalyticsDays(Number(e.target.value))}
                    className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200">
                    {[[1, 'Today'], [3, '3 days'], [7, '7 days'], [14, '14 days'], [30, '30 days'], [90, '90 days']]
                      .map(([d, l]) => <option key={d} value={d}>{l}</option>)}
                  </select>
                  <button onClick={fetchCosts} className="px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-cyan-500/40">Refresh</button>
                  <button onClick={() => downloadCsv(`/admin/usage/costs?days=${analyticsDays}&format=csv`, `costs_${analyticsDays}d.csv`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600/20 border border-cyan-500/30 text-sm text-cyan-300 hover:bg-cyan-600/30">
                    <Download className="w-3.5 h-3.5" /> CSV
                  </button>
                </div>
              </div>

              {analyticsLoading ? (
                <div className="flex items-center gap-3 text-slate-400"><div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" /> Loading…</div>
              ) : costsData ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Total (window)</div>
                      <div className="text-2xl font-bold text-cyan-300 mt-1 tabular-nums">$<CountUp value={costsData.total_usd} decimals={2} raw /></div>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Projected 30-day</div>
                      <div className="text-2xl font-bold text-amber-300 mt-1 tabular-nums">$<CountUp value={costsData.projected_30d_usd} decimals={2} raw /></div>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Days with spend</div>
                      <div className="text-2xl font-bold text-white mt-1 tabular-nums"><CountUp value={costsData.daily.length} /></div>
                    </div>
                  </div>

                  {/* Per-model share bars */}
                  <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-5">
                    <h3 className="text-sm font-semibold text-white mb-4">Cost by model</h3>
                    <div className="space-y-2.5">
                      {Object.entries(costsData.per_model).sort((a, b) => b[1] - a[1]).map(([m, c]) => {
                        const pct = costsData.total_usd > 0 ? (c / costsData.total_usd * 100) : 0;
                        return (
                          <div key={m} className="flex items-center gap-3 text-sm">
                            <span className="w-32 text-cyan-300 truncate">{m}</span>
                            <div className="flex-1 h-5 bg-slate-900/60 rounded overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-cyan-600 to-blue-500 rounded" style={{ width: `${Math.max(1, pct)}%` }} />
                            </div>
                            <span className="w-20 text-right tabular-nums text-slate-300">${c.toFixed(2)}</span>
                            <span className="w-14 text-right tabular-nums text-slate-500">{pct.toFixed(1)}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Daily table */}
                  <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl overflow-x-auto max-h-[420px] overflow-y-auto admin-scroll">
                    <table className="w-full text-sm min-w-[640px]">
                      <thead className="sticky top-0 bg-slate-900/95">
                        <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-700/60">
                          <th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Requests</th>
                          <th className="px-4 py-3 text-right">Cost</th><th className="px-4 py-3">Top models</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...costsData.daily].reverse().map(d => (
                          <tr key={d.date} className="border-b border-slate-800/50">
                            <td className="px-4 py-2.5 tabular-nums">{d.date}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{d.requests.toLocaleString()}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-cyan-300">${d.cost_usd.toFixed(2)}</td>
                            <td className="px-4 py-2.5 text-xs text-slate-400">
                              {Object.entries(d.models).sort((a, b) => b[1] - a[1]).slice(0, 3)
                                .map(([m, c]) => `${m} $${c.toFixed(2)}`).join(' · ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : <p className="text-slate-500 text-sm">No data.</p>}
            </div>
          )}

          {/* ════════ Adoption (modes, BYOK, versions, quota pressure) ════════ */}
          {activeTab === 'adoption' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-white">Adoption</h2>
                  <p className="text-sm text-slate-400 mt-1">Feature usage, client versions &amp; quota pressure</p>
                </div>
                <div className="flex items-center gap-2">
                  <select value={analyticsDays} onChange={e => setAnalyticsDays(Number(e.target.value))}
                    className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200">
                    {[[1, 'Today'], [3, '3 days'], [7, '7 days'], [14, '14 days'], [30, '30 days'], [90, '90 days']]
                      .map(([d, l]) => <option key={d} value={d}>{l}</option>)}
                  </select>
                  <button onClick={fetchAdoption} className="px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-cyan-500/40">Refresh</button>
                </div>
              </div>

              {analyticsLoading ? (
                <div className="flex items-center gap-3 text-slate-400"><div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" /> Loading…</div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Requests</div>
                      <div className="text-xl font-bold text-white mt-1 tabular-nums"><CountUp value={featuresData?.total_requests ?? 0} /></div>
                      <div className="text-xs text-slate-500 mt-1">{Object.entries(featuresData?.modes || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}</div>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">BYOK share</div>
                      <div className="text-xl font-bold text-cyan-300 mt-1 tabular-nums"><CountUp value={featuresData?.byok_pct ?? 0} decimals={1} raw suffix="%" /></div>
                      <div className="text-xs text-slate-500 mt-1">{featuresData?.byok_requests ?? 0} requests via user keys</div>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Users ≥60% quota</div>
                      <div className="text-xl font-bold text-amber-300 mt-1 tabular-nums"><CountUp value={quotaPressure?.count ?? 0} /></div>
                      <div className="text-xs text-slate-500 mt-1">conversion + support signal</div>
                    </div>
                  </div>

                  {/* Client versions */}
                  {versionsData && (
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-white mb-4">IDE client versions</h3>
                      <div className="space-y-2">
                        {versionsData.versions.map(v => (
                          <div key={v.version} className="flex items-center gap-3 text-sm">
                            <span className="w-32 text-cyan-300 truncate">{v.version}</span>
                            <div className="flex-1 h-5 bg-slate-900/60 rounded overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-blue-600 to-indigo-500 rounded" style={{ width: `${Math.max(1, v.pct)}%` }} />
                            </div>
                            <span className="w-24 text-right tabular-nums text-slate-300">{v.requests.toLocaleString()} reqs</span>
                            <span className="w-20 text-right tabular-nums text-slate-500">{v.users} users</span>
                          </div>
                        ))}
                        {versionsData.versions.every(v => v.version === 'unknown') && (
                          <p className="text-xs text-slate-500">All events predate client-version tracking — appears once the extension sends <code>x-client-version</code>.</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Quota pressure */}
                  {quotaPressure && quotaPressure.users.length > 0 && (
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl overflow-x-auto">
                      <table className="w-full text-sm min-w-[640px]">
                        <thead>
                          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-700/60">
                            <th className="px-4 py-3">User</th><th className="px-4 py-3">Plan</th>
                            <th className="px-4 py-3">Period</th><th className="px-4 py-3 text-right">Used</th>
                            <th className="px-4 py-3 text-right">Limit</th><th className="px-4 py-3 text-right">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {quotaPressure.users.map(u => (
                            <tr key={`${u.user_id}-${u.period}`} className="border-b border-slate-800/50">
                              <td className="px-4 py-2.5 text-slate-200">{u.email || u.user_id.slice(0, 10)}</td>
                              <td className="px-4 py-2.5 text-slate-400">{u.plan}</td>
                              <td className="px-4 py-2.5 text-slate-400">{u.period}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums">{formatTokens(u.used)}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums">{formatTokens(u.limit)}</td>
                              <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${u.pct >= 100 ? 'text-red-400' : u.pct >= 90 ? 'text-amber-400' : 'text-slate-300'}`}>{u.pct}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ════════ Alerts ════════ */}
          {activeTab === 'alerts' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-white">Alerts</h2>
                  <p className="text-sm text-slate-400 mt-1">Cost spikes, quota pressure &amp; anomalies — computed live</p>
                </div>
                <button onClick={fetchAlerts} className="px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-cyan-500/40">Refresh</button>
              </div>
              {analyticsLoading ? (
                <div className="flex items-center gap-3 text-slate-400"><div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" /> Loading…</div>
              ) : alertsData ? (
                alertsData.alerts.length === 0 ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl px-5 py-4 text-green-300 text-sm flex items-center gap-3">
                    <CheckCircle className="w-5 h-5" /> All clear — no anomalies detected.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alertsData.alerts.map((a, i) => (
                      <div key={i} className={`rounded-xl px-5 py-4 text-sm flex items-start gap-3 border ${
                        a.severity === 'high' ? 'bg-red-500/10 border-red-500/30 text-red-300'
                        : a.severity === 'medium' ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                        : 'bg-slate-800/40 border-slate-700/60 text-slate-300'}`}>
                        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                        <div>
                          <div className="font-semibold capitalize">{a.kind.replace(/_/g, ' ')}</div>
                          <div className="text-xs opacity-80 mt-0.5">{a.message}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : <p className="text-slate-500 text-sm">No data.</p>}

              {/* Quota-pressure users — the people behind the alert */}
              {quotaPressure && quotaPressure.users.length > 0 && (
                <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-700/60 flex items-center gap-2">
                    <Gauge className="w-4 h-4 text-amber-400" />
                    <h3 className="text-sm font-semibold text-white">Users at quota pressure</h3>
                    <span className="text-xs text-slate-500 ml-2">{quotaPressure.count} user(s) ≥60% of limit</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[640px]">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-700/60">
                          <th className="px-4 py-3">User</th><th className="px-4 py-3">Plan</th>
                          <th className="px-4 py-3">Period</th><th className="px-4 py-3 text-right">Used</th>
                          <th className="px-4 py-3 text-right">Limit</th><th className="px-4 py-3 text-right">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quotaPressure.users.map(u => (
                          <tr key={`${u.user_id}-${u.period}`} className="border-b border-slate-800/50">
                            <td className="px-4 py-2.5 text-slate-200">{u.email || u.user_id.slice(0, 10)}</td>
                            <td className="px-4 py-2.5 text-slate-400">{u.plan}</td>
                            <td className="px-4 py-2.5 text-slate-400">{u.period}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{formatTokens(u.used)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{formatTokens(u.limit)}</td>
                            <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${u.pct >= 100 ? 'text-red-400' : u.pct >= 90 ? 'text-amber-400' : 'text-slate-300'}`}>{u.pct}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════════ Ops (live feed + runaway + errors) ════════ */}
          {activeTab === 'ops' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-white">Operations</h2>
                  <p className="text-sm text-slate-400 mt-1">Live request feed, runaway conversations &amp; errors</p>
                </div>
                <div className="flex items-center gap-2">
                  <select value={analyticsDays} onChange={e => setAnalyticsDays(Number(e.target.value))}
                    className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200">
                    {[[1, 'Today'], [3, '3 days'], [7, '7 days'], [14, '14 days'], [30, '30 days']]
                      .map(([d, l]) => <option key={d} value={d}>{l}</option>)}
                  </select>
                  <button onClick={fetchOps} className="px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-cyan-500/40">Refresh</button>
                </div>
              </div>

              {/* Runaway conversations */}
              {runawayData && (
                <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-700/60 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <h3 className="text-sm font-semibold text-white">Runaway conversations</h3>
                    <span className="text-xs text-slate-500 ml-2">{runawayData.count} convs ≥{runawayData.min_turns} turns or ≥${runawayData.min_cost_usd}</span>
                  </div>
                  {runawayData.conversations.length === 0 ? (
                    <p className="px-5 py-4 text-xs text-slate-500">None — agent loops are behaving.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm min-w-[720px]">
                        <thead>
                          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-700/60">
                            <th className="px-4 py-2.5">User</th><th className="px-4 py-2.5">Model</th>
                            <th className="px-4 py-2.5 text-right">Turns</th><th className="px-4 py-2.5 text-right">Input</th>
                            <th className="px-4 py-2.5 text-right">Cost</th><th className="px-4 py-2.5 text-right">Span</th>
                            <th className="px-4 py-2.5">Started</th>
                          </tr>
                        </thead>
                        <tbody>
                          {runawayData.conversations.map(c => (
                            <tr key={c.conversation_id} className="border-b border-slate-800/50">
                              <td className="px-4 py-2 text-slate-200">{c.email || c.user_id.slice(0, 8)}</td>
                              <td className="px-4 py-2 text-cyan-300">{c.model}</td>
                              <td className={`px-4 py-2 text-right tabular-nums font-semibold ${c.turns >= 100 ? 'text-red-400' : 'text-amber-400'}`}>{c.turns}</td>
                              <td className="px-4 py-2 text-right tabular-nums">{formatTokens(c.input_tokens)}</td>
                              <td className="px-4 py-2 text-right tabular-nums text-cyan-300">${c.cost_usd.toFixed(2)}</td>
                              <td className="px-4 py-2 text-right tabular-nums">{c.span_minutes}m</td>
                              <td className="px-4 py-2 text-xs text-slate-500">{(c.first || '').slice(0, 16).replace('T', ' ')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Errors */}
              {errorsData && (
                <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-1">
                    Request errors
                    <span className={`ml-3 text-xs font-normal ${errorsData.error_rate_pct > 2 ? 'text-red-400' : 'text-slate-500'}`}>
                      {errorsData.total_errors} errors · {errorsData.error_rate_pct}% of requests
                    </span>
                  </h3>
                  {errorsData.total_errors === 0 ? (
                    <p className="text-xs text-slate-500 mt-1">No errors recorded — tracking began recently or all requests succeeded.</p>
                  ) : (
                    <>
                      <div className="flex gap-6 text-xs text-slate-400 my-3">
                        {Object.entries(errorsData.by_model).map(([m, n]) => <span key={m}><b className="text-white">{m}</b> {n}</span>)}
                      </div>
                      <div className="max-h-48 overflow-y-auto admin-scroll space-y-1.5">
                        {errorsData.recent.map((e, i) => (
                          <div key={i} className="text-xs bg-slate-900/50 rounded-lg px-3 py-2 flex gap-3">
                            <span className="text-slate-500 shrink-0">{(e.timestamp || '').slice(11, 19)}</span>
                            <span className="text-cyan-300 shrink-0 w-28 truncate">{e.model}</span>
                            <span className="text-slate-400 truncate">{e.error}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Live feed */}
              {liveFeed && (
                <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-700/60 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <h3 className="text-sm font-semibold text-white">Live request feed</h3>
                    <span className="text-xs text-slate-500 ml-2">auto-refresh 8s</span>
                  </div>
                  <div className="overflow-x-auto max-h-[420px] overflow-y-auto admin-scroll">
                    <table className="w-full text-xs min-w-[720px]">
                      <thead className="sticky top-0 bg-slate-900/95">
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-700/60">
                          <th className="px-3 py-2">Time</th><th className="px-3 py-2">User</th>
                          <th className="px-3 py-2">Model</th><th className="px-3 py-2 text-right">In</th>
                          <th className="px-3 py-2 text-right">Out</th><th className="px-3 py-2 text-right">Cache</th>
                          <th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2">Stop</th>
                          <th className="px-3 py-2">Conv</th>
                        </tr>
                      </thead>
                      <tbody>
                        {liveFeed.events.map((e, i) => (
                          <tr key={i} className="border-b border-slate-800/50">
                            <td className="px-3 py-1.5 text-slate-500 tabular-nums">{e.ts.slice(11, 19)}</td>
                            <td className="px-3 py-1.5 text-slate-300">{e.user}</td>
                            <td className="px-3 py-1.5 text-cyan-300">{e.model}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{formatTokens(e.in)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{formatTokens(e.out)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{formatTokens(e.cached)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">${(e.cost || 0).toFixed(3)}</td>
                            <td className="px-3 py-1.5 text-slate-500">{e.stop}</td>
                            <td className="px-3 py-1.5 text-slate-600 font-mono">{e.conv}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════════ Revenue ════════ */}
          {activeTab === 'revenue' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-white">Revenue</h2>
                  <p className="text-sm text-slate-400 mt-1">Plans, estimated MRR &amp; payments (90-day window)</p>
                </div>
                <button onClick={fetchRevenue} className="px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-cyan-500/40">Refresh</button>
              </div>
              {analyticsLoading ? (
                <div className="flex items-center gap-3 text-slate-400"><div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" /> Loading…</div>
              ) : revenueData ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Est. MRR</div>
                      <div className="text-2xl font-bold text-cyan-300 mt-1 tabular-nums">$<CountUp value={revenueData.estimated_mrr_usd} decimals={0} raw /></div>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Paid conversion</div>
                      <div className="text-2xl font-bold text-white mt-1 tabular-nums"><CountUp value={revenueData.paid_conversion_pct} decimals={1} raw suffix="%" /></div>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Payments (90d)</div>
                      <div className="text-2xl font-bold text-white mt-1 tabular-nums"><CountUp value={revenueData.payments_in_window} /></div>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Collected</div>
                      <div className="text-2xl font-bold text-green-300 mt-1 tabular-nums">$<CountUp value={revenueData.payment_total_usd} decimals={0} raw /></div>
                    </div>
                  </div>

                  <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-5">
                    <h3 className="text-sm font-semibold text-white mb-4">Plan distribution</h3>
                    <div className="space-y-2">
                      {Object.entries(revenueData.plan_distribution).map(([p, n]) => {
                        const total = Object.values(revenueData.plan_distribution).reduce((a, b) => a + b, 0);
                        const pct = total ? n / total * 100 : 0;
                        return (
                          <div key={p} className="flex items-center gap-3 text-sm">
                            <span className="w-24 text-cyan-300 capitalize">{p}</span>
                            <div className="flex-1 h-5 bg-slate-900/60 rounded overflow-hidden">
                              <div className={`h-full rounded ${p === 'free' ? 'bg-slate-600' : 'bg-gradient-to-r from-cyan-600 to-blue-500'}`} style={{ width: `${Math.max(1, pct)}%` }} />
                            </div>
                            <span className="w-16 text-right tabular-nums text-slate-300">{n} users</span>
                            <span className="w-14 text-right tabular-nums text-slate-500">{pct.toFixed(1)}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {revenueData.recent_payments.length > 0 && (
                    <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl overflow-x-auto">
                      <table className="w-full text-sm min-w-[560px]">
                        <thead>
                          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-700/60">
                            <th className="px-4 py-3">Date</th><th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3">Plan</th><th className="px-4 py-3 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenueData.recent_payments.map((p, i) => (
                            <tr key={i} className="border-b border-slate-800/50">
                              <td className="px-4 py-2.5 text-slate-400 tabular-nums">{(p.ts || '').slice(0, 10)}</td>
                              <td className="px-4 py-2.5 text-slate-200">{p.email}</td>
                              <td className="px-4 py-2.5 text-cyan-300 capitalize">{p.plan}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums">${p.amount.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : <p className="text-slate-500 text-sm">No data.</p>}
            </div>
          )}

          {/* ════════ Config (plan limits + model gating) ════════ */}
          {activeTab === 'config' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-white">Plan Config</h2>
                  <p className="text-sm text-slate-400 mt-1">Override credit limits &amp; model gating per plan — applies on next quota check, no deploy needed</p>
                </div>
                <button onClick={fetchConfig} className="px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-cyan-500/40">Refresh</button>
              </div>
              {planConfig ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {Object.entries(planConfig.plans).map(([plan, def]) => (
                    <PlanCard key={plan} plan={plan} def={def} onSave={savePlanOverride} />
                  ))}
                </div>
              ) : <p className="text-slate-500 text-sm">Loading…</p>}
            </div>
          )}

          {/* ════════ Broadcast ════════ */}
          {activeTab === 'broadcast' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div>
                <h2 className="text-2xl font-bold text-white">Broadcast Banner</h2>
                <p className="text-sm text-slate-400 mt-1">Message shown to IDE users — maintenance, notices, announcements</p>
              </div>
              <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-5 space-y-4">
                <textarea value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)} rows={3}
                  placeholder="e.g. Scheduled maintenance Saturday 02:00 UTC — expect brief outages"
                  className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-600 focus:border-cyan-500/50 outline-none resize-none" />
                <div className="flex items-center gap-3 flex-wrap">
                  <select value={broadcastSev} onChange={e => setBroadcastSev(e.target.value)}
                    className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200">
                    <option value="info">Info (blue)</option>
                    <option value="warning">Warning (amber)</option>
                    <option value="critical">Critical (red)</option>
                  </select>
                  <select value={broadcastHours} onChange={e => setBroadcastHours(Number(e.target.value))}
                    className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200">
                    {[6, 24, 48, 72, 168].map(h => <option key={h} value={h}>Expires in {h}h</option>)}
                  </select>
                  <button onClick={postBroadcast}
                    className="px-4 py-1.5 rounded-lg bg-cyan-600/30 border border-cyan-500/40 text-sm text-cyan-200 hover:bg-cyan-600/40">Publish</button>
                  <button onClick={clearBroadcast}
                    className="px-4 py-1.5 rounded-lg bg-red-600/15 border border-red-500/30 text-sm text-red-300 hover:bg-red-600/25">Clear active</button>
                </div>
              </div>
              {banners.length > 0 && (
                <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-slate-700/60 text-sm font-semibold text-white">History</div>
                  {banners.map((b, i) => (
                    <div key={i} className="px-5 py-3 border-b border-slate-800/50 flex items-center gap-3 text-sm">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${b.active ? 'bg-green-400' : 'bg-slate-600'}`} />
                      <span className={`text-xs px-2 py-0.5 rounded ${b.severity === 'critical' ? 'bg-red-500/20 text-red-300' : b.severity === 'warning' ? 'bg-amber-500/20 text-amber-300' : 'bg-blue-500/20 text-blue-300'}`}>{b.severity}</span>
                      <span className="flex-1 text-slate-300 truncate">{b.message}</span>
                      <span className="text-xs text-slate-500">{(b.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              {selectedUser ? (
                /* ── User Detail View ── */
                <div className="space-y-5">
                  <button
                    onClick={closeUserDetail}
                    className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to Users
                  </button>

                  {/* User header */}
                  <div className="admin-table-container p-6">
                    <div className="flex items-start gap-4 flex-wrap">
                      <div className="relative shrink-0">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ background: 'linear-gradient(135deg, #2563eb, #06b6d4)' }}>
                          {selectedUser.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        {selectedUser.is_pro && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-cyan-400 border-2 border-[#0d111c]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-xl font-bold text-white">{selectedUser.name}</h2>
                        <p className="text-sm text-slate-400">{selectedUser.email}</p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${PLAN_BADGE[selectedUser.plan] || PLAN_BADGE.free}`}>
                            <span className="capitalize">{selectedUser.plan || 'free'}</span>
                          </span>
                          <span className="text-xs text-slate-500">
                            {selectedUser.is_pro ? (
                              <span className="inline-flex items-center gap-1.5 text-green-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-400" style={{ boxShadow: '0 0 6px rgba(74, 222, 128, 0.4)' }} /> Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-slate-500">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-600" /> Inactive
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-slate-500">
                            Joined {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                          </span>
                          {selectedUser.plan_expired ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/30">
                              {selectedUser.billing_type && selectedUser.billing_type !== 'none' ? 'Subscription ended' : 'Trial expired'}
                            </span>
                          ) : selectedUser.plan_expires_at ? (
                            <span className="text-xs text-slate-500">
                              {(selectedUser.plan || 'free') === 'free' ? 'Trial ends' : 'Expires'}{' '}
                              {new Date(selectedUser.plan_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              {selectedUser.days_left !== null && selectedUser.days_left !== undefined && ` (${selectedUser.days_left}d)`}
                            </span>
                          ) : selectedUser.manual_grant && (selectedUser.plan || 'free') !== 'free' ? (
                            <span className="text-xs text-slate-500">Manual grant · no expiry</span>
                          ) : null}
                          {selectedUser.is_blocked && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/30">Suspended</span>
                          )}
                          {selectedUser.role === 'admin' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-purple-500/15 text-purple-400 border border-purple-500/30"><Shield className="w-3 h-3" /> Admin</span>
                          )}
                        </div>
                      </div>
                      {/* Account actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => extendUserPlan(selectedUser.id, selectedUser.email)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 transition-all"
                          title="Extend paid plan by 30 days (stacks on remaining time; restores pro if expired)"
                        >
                          Extend +1 mo
                        </button>
                        <button
                          onClick={() => suspendUser(selectedUser.id, selectedUser.email, !selectedUser.is_blocked)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${selectedUser.is_blocked
                            ? 'bg-green-500/10 text-green-400 border-green-500/30 hover:bg-green-500/20'
                            : 'bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20'}`}
                        >
                          {selectedUser.is_blocked ? 'Unsuspend' : 'Suspend'}
                        </button>
                        <button
                          onClick={() => forceLogoutUser(selectedUser.id, selectedUser.email)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-700/40 text-slate-300 border border-slate-600/40 hover:bg-slate-700/60 transition-all"
                        >
                          Force Logout
                        </button>
                        <button
                          onClick={() => setUserRole(selectedUser.id, selectedUser.email, selectedUser.role !== 'admin')}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/30 hover:bg-purple-500/20 transition-all"
                        >
                          {selectedUser.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* User 360 — lifetime usage & activity */}
                  {user360 && (
                    <div className="admin-table-container p-5">
                      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h3 className="text-sm font-semibold text-white">Lifetime usage</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${
                          user360.churn_risk === 'high' ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                          : user360.churn_risk === 'medium' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                          : 'bg-green-500/15 text-green-400 border border-green-500/30'}`}>
                          {user360.churn_risk === 'high' ? 'Churn risk: high' : user360.churn_risk === 'medium' ? 'Churn risk: medium' : 'Churn risk: low'}
                          {user360.lifetime.days_idle != null && ` · idle ${user360.lifetime.days_idle}d`}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                          { label: 'Requests', num: user360.lifetime.requests },
                          { label: 'Input tokens', num: user360.lifetime.input_tokens },
                          { label: 'Output tokens', num: user360.lifetime.output_tokens },
                          { label: 'Est. lifetime cost', num: user360.lifetime.cost_usd, raw: true, decimals: 2, prefix: '$' },
                          { label: 'Conversations', num: user360.lifetime.conversations },
                          { label: 'Login sessions', num: user360.login_sessions },
                          { label: 'Payments', num: user360.payments },
                          { label: 'Last active', v: user360.lifetime.last_active ? new Date(user360.lifetime.last_active).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—' },
                        ].map(s => (
                          <div key={s.label} className="bg-slate-900/40 rounded-lg p-3">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.label}</div>
                            <div className="text-base font-bold text-white mt-0.5 tabular-nums">
                              {s.num != null
                                ? <CountUp value={s.num} raw={s.raw} decimals={s.decimals || 0} prefix={s.prefix || ''} />
                                : s.v}
                            </div>
                          </div>
                        ))}
                      </div>
                      {user360.models.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-4">
                          {user360.models.map(m => (
                            <span key={m.model} className="text-[11px] px-2 py-1 rounded-md bg-slate-900/50 border border-slate-700/60 text-slate-300">
                              <span className="text-cyan-300">{m.model}</span> · {m.requests} reqs · ${m.cost_usd.toFixed(2)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Live quota usage (server counters — same source that enforces) */}
                  {userQuota && (
                    <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4 mb-4">
                      <div className="text-sm font-medium text-slate-200 mb-3">Live Quota Usage <span className="text-slate-500 font-normal text-xs">(credit counters — 1cr = $0.01 API cost)</span></div>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { label: 'Today', d: userQuota.daily },
                          { label: 'This week', d: userQuota.weekly },
                        ].map(({ label, d }) => {
                          const p = d.limit > 0 ? Math.min(100, Math.round((d.used || 0) * 100 / d.limit)) : null;
                          return (
                            <div key={label}>
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-slate-400">{label}</span>
                                <span className={p !== null && p >= 80 ? 'text-amber-400' : 'text-slate-300'}>
                                  {d.limit === -1 ? '∞' : `${(d.used || 0).toFixed(1)} / ${d.limit} cre (${p}%)`}
                                </span>
                              </div>
                              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${p !== null && p >= 100 ? 'bg-red-400' : p !== null && p >= 80 ? 'bg-amber-400' : 'bg-cyan-400'}`} style={{ width: `${p ?? 0}%` }} />
                              </div>
                              <div className="text-[10px] text-slate-500 mt-1">
                                {d.reset_at ? `resets ${new Date(d.reset_at).toLocaleString()}` : ''}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Credit quota override — effective immediately on save */}
                  <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-slate-200">AI Credit Quota <span className="text-slate-500 font-normal">(per-user override — beats plan default)</span></span>
                      {quotaSaved && <span className="text-xs text-green-400">{quotaSaved}</span>}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { key: 'daily', label: 'Daily limit (credits)', def: PLAN_CREDIT_DEFAULTS[selectedUser.plan || 'free']?.d },
                        { key: 'weekly', label: 'Weekly limit (credits)', def: PLAN_CREDIT_DEFAULTS[selectedUser.plan || 'free']?.w },
                      ].map(({ key, label, def }) => {
                        const unlimited = quotaDraft[key + 'Unlimited'];
                        return (
                          <div key={key}>
                            <div className="flex items-center justify-between mb-1.5">
                              <label className="text-xs text-slate-400">{label}</label>
                              <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={unlimited}
                                  onChange={(e) => setQuotaDraft(d => ({ ...d, [key + 'Unlimited']: e.target.checked }))}
                                  className="accent-cyan-500"
                                />
                                Unlimited
                              </label>
                            </div>
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              disabled={unlimited}
                              value={quotaDraft[key]}
                              onChange={(e) => setQuotaDraft(d => ({ ...d, [key]: e.target.value }))}
                              placeholder={unlimited ? '∞' : (def === -1 ? '∞ (plan: unlimited)' : `${def} (plan default)`)}
                              className="w-full bg-slate-900/60 border border-slate-700/60 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 disabled:opacity-40"
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-slate-500">
                        Current: {selectedUser.quota_daily_override === -1 ? 'unlimited' : selectedUser.quota_daily_override != null ? `${selectedUser.quota_daily_override} cre` : `plan default (${PLAN_CREDIT_DEFAULTS[selectedUser.plan || 'free']?.d === -1 ? 'unlimited' : (PLAN_CREDIT_DEFAULTS[selectedUser.plan || 'free']?.d ?? 35) + ' cre'})`}/day
                        · empty = plan default
                      </span>
                      <button
                        onClick={() => saveUserQuota(selectedUser.id)}
                        disabled={savingQuota}
                        className="px-4 py-1.5 rounded-lg text-xs font-medium bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/25 transition-all disabled:opacity-50"
                      >
                        {savingQuota ? 'Saving…' : 'Save Quota'}
                      </button>
                    </div>
                  </div>

                  {/* Date filter */}
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-400 mr-1">Date Range:</span>
                    {[
                      { label: '7 Days', value: 7 },
                      { label: '30 Days', value: 30 },
                      { label: '90 Days', value: 90 },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => changeUsageDays(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          usageDays === opt.value
                            ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                            : 'bg-slate-800/40 text-slate-400 hover:text-white border border-transparent'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Usage content */}
                  {usageLoading ? (
                    <div className="flex items-center gap-2 text-slate-400 text-sm py-12">
                      <div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
                      Loading usage details...
                    </div>
                  ) : userUsage ? (
                    <>
                      {/* Summary stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="admin-table-container p-4">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Total Tokens</p>
                          <p className="text-2xl font-bold text-white tabular-nums">{formatTokens(userUsage.totals.tokens)}</p>
                        </div>
                        <div className="admin-table-container p-4">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Input Tokens</p>
                          <p className="text-2xl font-bold text-blue-400 tabular-nums">{formatTokens(userUsage.totals.input_tokens)}</p>
                        </div>
                        <div className="admin-table-container p-4">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Output Tokens</p>
                          <p className="text-2xl font-bold text-cyan-400 tabular-nums">{formatTokens(userUsage.totals.output_tokens)}</p>
                        </div>
                        <div className="admin-table-container p-4">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Requests</p>
                          <p className="text-2xl font-bold text-white tabular-nums">{userUsage.totals.requests.toLocaleString()}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="admin-table-container p-4">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Credits used</p>
                          <p className="text-2xl font-bold text-violet-400 tabular-nums">{(userUsage.totals.credits ?? 0).toFixed(1)}</p>
                        </div>
                        <div className="admin-table-container p-4">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Est. cost</p>
                          <p className="text-2xl font-bold text-cyan-300 tabular-nums">{formatCost(userUsage.totals.est_cost_usd)}</p>
                        </div>
                      </div>

                      {/* Daily usage chart */}
                      <div className="admin-table-container p-5">
                        <h3 className="text-sm font-medium text-slate-300 mb-4">Daily Token Usage</h3>
                        <div className="flex items-end gap-[3px] h-32">
                          {userUsage.daily.map((d) => {
                            const maxDay = Math.max(...userUsage.daily.map(x => x.tokens), 1);
                            const h = Math.max(2, Math.round((d.tokens / maxDay) * 100));
                            return (
                              <div key={d.date} className="flex-1 h-full flex items-end group/bar relative" style={{ minWidth: '4px' }} title={`${d.date}: ${formatTokens(d.tokens)} tokens, ${d.requests} requests`}>
                                <div
                                  className={`w-full rounded-sm transition-colors ${d.tokens > 0 ? 'bg-gradient-to-t from-blue-600/60 to-cyan-400/60 hover:from-blue-500 hover:to-cyan-300' : 'bg-slate-800/40'}`}
                                  style={{ height: `${h}%` }}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-between mt-2 text-[10px] text-slate-600">
                          <span>{userUsage.daily[0]?.date}</span>
                          <span>{userUsage.daily[userUsage.daily.length - 1]?.date}</span>
                        </div>
                      </div>

                      {/* Daily breakdown table */}
                      <div className="admin-table-container overflow-hidden">
                        <div className="px-5 py-3.5 border-b border-slate-800/60">
                          <h3 className="text-sm font-medium text-slate-300">Day-wise Breakdown</h3>
                        </div>
                        <div className="admin-scroll overflow-x-auto" style={{ maxHeight: '300px' }}>
                          <table className="w-full">
                            <thead className="sticky top-0 z-10" style={{ background: 'rgba(13, 17, 28, 0.98)' }}>
                              <tr className="border-b border-slate-700/50">
                                <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                                <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Tokens</th>
                                <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Input</th>
                                <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Output</th>
                                <th className="text-right px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Requests</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/40">
                              {[...userUsage.daily].reverse().map((d) => (
                                <tr key={d.date} className="hover:bg-slate-800/30 transition-colors">
                                  <td className="px-5 py-2.5 text-sm text-slate-300">{d.date}</td>
                                  <td className="px-4 py-2.5 text-sm text-white tabular-nums text-right font-medium">{formatTokens(d.tokens)}</td>
                                  <td className="px-4 py-2.5 text-sm text-blue-400 tabular-nums text-right hidden sm:table-cell">{formatTokens(d.input_tokens)}</td>
                                  <td className="px-4 py-2.5 text-sm text-cyan-400 tabular-nums text-right hidden sm:table-cell">{formatTokens(d.output_tokens)}</td>
                                  <td className="px-5 py-2.5 text-sm text-slate-400 tabular-nums text-right">{d.requests}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Model breakdown */}
                      <div className="admin-table-container p-5">
                        <h3 className="text-sm font-medium text-slate-300 mb-4">Model-wise Breakdown</h3>
                        {userUsage.model_breakdown.length === 0 ? (
                          <p className="text-sm text-slate-600 py-4 text-center">No model usage data available.</p>
                        ) : (
                          <div className="space-y-3">
                            {userUsage.model_breakdown.map((m) => (
                              <div key={m.model} className="flex items-center gap-4">
                                <span className="text-sm text-slate-300 w-40 truncate font-medium">{m.model}</span>
                                <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all"
                                    style={{ width: `${m.percentage}%` }}
                                  />
                                </div>
                                <span className="text-sm text-white tabular-nums w-20 text-right font-medium">{formatTokens(m.tokens)}</span>
                                <span className="text-xs text-slate-500 tabular-nums w-16 text-right">{m.requests} req</span>
                                <span className="text-xs text-slate-600 tabular-nums w-12 text-right">{m.percentage}%</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="admin-table-container p-12 text-center">
                      <p className="text-slate-500">No usage data available.</p>
                    </div>
                  )}
                </div>
              ) : (
                /* ── User List View ── */
                <>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-white">User Management</h2>
                    <p className="text-sm text-slate-400 mt-1">{usersTotal} total registered users</p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <select
                      value={planFilter}
                      onChange={(e) => { setPlanFilter(e.target.value); setUsersPage(0); }}
                      className="bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-cyan-500/50"
                    >
                      <option value="">All plans</option>
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                      <option value="team">Team</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                    <select
                      value={blockedFilter}
                      onChange={(e) => { setBlockedFilter(e.target.value); setUsersPage(0); }}
                      className="bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-cyan-500/50"
                    >
                      <option value="">All status</option>
                      <option value="false">Active</option>
                      <option value="true">Suspended</option>
                    </select>
                    <select
                      value={userSort}
                      onChange={(e) => { setUserSort(e.target.value); setUsersPage(0); }}
                      className="bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-cyan-500/50"
                    >
                      <option value="created">Newest first</option>
                      <option value="usage">Top token usage</option>
                    </select>
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setUsersPage(0); }}
                        placeholder="Search name or email..."
                        className="bg-slate-800/60 border border-slate-700/60 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 w-64"
                      />
                    </div>
                  </div>
                </div>

              {loading ? (
                <div className="flex items-center gap-3 text-slate-400">
                  <div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
                  Loading...
                </div>
              ) : usersList.length === 0 ? (
                <div className="dl-card rounded-2xl p-12 text-center">
                  <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No users found.</p>
                </div>
              ) : (
                <>
                  <div className="admin-table-container">
                    <div className="admin-scroll overflow-x-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                      <table className="w-full">
                        <thead className="sticky top-0 z-10" style={{ background: 'rgba(13, 17, 28, 0.98)', backdropFilter: 'blur(8px)' }}>
                          <tr className="border-b border-slate-700/50">
                            <th className="text-left px-5 py-3.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">User</th>
                            <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">Plan</th>
                            <th className="text-right px-4 py-3.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">Token Usage</th>
                            <th className="text-right px-4 py-3.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">Requests</th>
                            <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">Joined</th>
                            <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                            <th className="text-right px-5 py-3.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/40">
                          {usersList.map((u) => {
                            const totalTokens = u.total_tokens || 0;
                            const totalRequests = u.total_requests || 0;
                            const maxTokens = Math.max(...usersList.map(x => x.total_tokens || 0), 1);
                            const tokenPct = Math.round((totalTokens / maxTokens) * 100);
                            return (
                              <tr key={u.id} className="hover:bg-slate-800/30 transition-colors group cursor-pointer" onClick={() => openUserDetail(u)}>
                                {/* User */}
                                <td className="px-5 py-3.5">
                                  <div className="flex items-center gap-3">
                                    <div className="relative shrink-0">
                                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-xs" style={{ background: 'linear-gradient(135deg, #2563eb, #06b6d4)' }}>
                                        {u.name?.charAt(0).toUpperCase() || '?'}
                                      </div>
                                      {u.is_pro && (
                                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-cyan-400 border-2 border-[#0d111c]" />
                                      )}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-white truncate">{u.name}</p>
                                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                                    </div>
                                  </div>
                                </td>

                                {/* Plan */}
                                <td className="px-4 py-3.5 hidden md:table-cell" onClick={(e) => e.stopPropagation()}>
                                  {editingPlan === u.id ? (
                                    <select
                                      value={u.plan}
                                      onChange={(e) => updateUserPlan(u.id, e.target.value)}
                                      onBlur={() => setEditingPlan(null)}
                                      className="bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
                                      autoFocus
                                    >
                                      <option value="free">Free</option>
                                      <option value="pro">Pro</option>
                                      <option value="team">Team</option>
                                      <option value="enterprise">Enterprise</option>
                                    </select>
                                  ) : (
                                    <div>
                                      <button
                                        onClick={() => setEditingPlan(u.id)}
                                        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium transition-all ${PLAN_BADGE[u.plan] || PLAN_BADGE.free} hover:ring-1 hover:ring-white/10`}
                                      >
                                        <span className="capitalize">{u.plan || 'free'}</span>
                                      </button>
                                      {/* Plan expiry */}
                                      {u.plan_expired ? (
                                        <div className="text-[10px] font-medium text-red-400 mt-1">
                                          Expired{u.billing_type && u.billing_type !== 'none' ? ' · sub ended' : ' · trial over'}
                                        </div>
                                      ) : u.plan_expires_at && u.plan && u.plan !== 'free' ? (
                                        <div className={`text-[10px] mt-1 ${(u.days_left ?? 99) <= 3 ? 'text-amber-400 font-medium' : 'text-slate-500'}`}>
                                          Expires {new Date(u.plan_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                          {u.days_left !== null && u.days_left !== undefined && ` · ${u.days_left}d left`}
                                        </div>
                                      ) : u.plan_expires_at && u.plan === 'free' ? (
                                        <div className={`text-[10px] mt-1 ${(u.days_left ?? 99) <= 2 ? 'text-amber-400 font-medium' : 'text-slate-500'}`}>
                                          Trial {u.days_left === 0 ? 'ends today' : `· ${u.days_left}d left`}
                                        </div>
                                      ) : u.manual_grant && u.plan && u.plan !== 'free' ? (
                                        <div className="text-[10px] mt-1 text-slate-500">
                                          Manual grant · no expiry
                                        </div>
                                      ) : null}
                                    </div>
                                  )}
                                </td>

                                {/* Token Usage */}
                                <td className="px-4 py-3.5 hidden lg:table-cell">
                                  <div className="flex items-center justify-end gap-2.5">
                                    <div className="w-20 h-1 rounded-full bg-slate-800 overflow-hidden">
                                      <div
                                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all"
                                        style={{ width: `${tokenPct}%` }}
                                      />
                                    </div>
                                    <span className="text-sm font-medium text-white tabular-nums whitespace-nowrap" style={{ minWidth: '3rem', textAlign: 'right' }}>
                                      {formatTokens(totalTokens)}
                                    </span>
                                  </div>
                                </td>

                                {/* Requests */}
                                <td className="px-4 py-3.5 hidden lg:table-cell text-right">
                                  <span className="text-sm text-slate-300 tabular-nums">{totalRequests.toLocaleString()}</span>
                                </td>

                                {/* Joined */}
                                <td className="px-4 py-3.5 hidden md:table-cell">
                                  <span className="text-xs text-slate-500">
                                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                                  </span>
                                </td>

                                {/* Status */}
                                <td className="px-4 py-3.5">
                                  <div className="flex items-center gap-2">
                                    {u.is_blocked ? (
                                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-400">
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                                        Suspended
                                      </span>
                                    ) : u.plan_expired ? (
                                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-400">
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                                        Expired
                                      </span>
                                    ) : u.is_pro ? (
                                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-400">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-400" style={{ boxShadow: '0 0 6px rgba(74, 222, 128, 0.4)' }} />
                                        Active
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                                        <span className="w-1.5 h-1.5 rounded-full bg-slate-600" />
                                        Inactive
                                      </span>
                                    )}
                                    {u.role === 'admin' && (
                                      <span title="Admin"><Shield className="w-3 h-3 text-purple-400" /></span>
                                    )}
                                  </div>
                                </td>

                                {/* Actions */}
                                <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {editingPlan === u.id ? (
                                      <button
                                        onClick={() => setEditingPlan(null)}
                                        className="px-2 py-1 text-xs text-slate-400 hover:text-white rounded-md hover:bg-slate-700/40 transition-colors"
                                      >
                                        Done
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => setEditingPlan(u.id)}
                                        className="px-2 py-1 text-xs text-cyan-400 hover:text-cyan-300 font-medium rounded-md hover:bg-cyan-500/10 transition-colors"
                                      >
                                        Edit
                                      </button>
                                    )}
                                    {(u.plan_expired || (u.plan && u.plan !== 'free')) && (
                                      <button
                                        onClick={() => extendUserPlan(u.id, u.email)}
                                        className="p-1 text-slate-500 hover:text-cyan-300 rounded-md hover:bg-cyan-500/10 transition-colors"
                                        title="Extend plan +30 days"
                                      >
                                        <Calendar className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => suspendUser(u.id, u.email, !u.is_blocked)}
                                      className="p-1 text-slate-500 hover:text-amber-400 rounded-md hover:bg-amber-500/10 transition-colors"
                                      title={u.is_blocked ? 'Unsuspend user' : 'Suspend user'}
                                    >
                                      <PowerOff className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => forceLogoutUser(u.id, u.email)}
                                      className="p-1 text-slate-500 hover:text-cyan-300 rounded-md hover:bg-cyan-500/10 transition-colors"
                                      title="Force logout (kill all sessions)"
                                    >
                                      <LogOut className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => deleteUser(u.id, u.name)}
                                      className="p-1 text-slate-500 hover:text-red-400 rounded-md hover:bg-red-500/10 transition-colors"
                                      title="Delete user"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {usersTotal > USERS_PER_PAGE && (
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <p className="text-sm text-slate-400">
                        Showing {usersPage * USERS_PER_PAGE + 1}–{Math.min((usersPage + 1) * USERS_PER_PAGE, usersTotal)} of {usersTotal}
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setUsersPage(Math.max(0, usersPage - 1))}
                          disabled={usersPage === 0}
                          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-slate-300 bg-slate-800/60 hover:bg-slate-700/60 disabled:opacity-30 transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" /> Prev
                        </button>
                        <span className="text-sm text-slate-400 px-2">Page {usersPage + 1}</span>
                        <button
                          onClick={() => setUsersPage(usersPage + 1)}
                          disabled={(usersPage + 1) * USERS_PER_PAGE >= usersTotal}
                          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-slate-300 bg-slate-800/60 hover:bg-slate-700/60 disabled:opacity-30 transition-colors"
                        >
                          Next <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Plan distribution ── */}
                  {stats?.plan_breakdown && Object.keys(stats.plan_breakdown).length > 0 && (
                    <div className="dl-card rounded-2xl p-5">
                      <h3 className="text-sm font-semibold text-slate-300 mb-4">Users by plan</h3>
                      <StackedBar parts={Object.entries(stats.plan_breakdown).map(([plan, n]) => ({
                        label: plan.charAt(0).toUpperCase() + plan.slice(1),
                        value: n,
                        color: { free: '#64748b', pro: '#22d3ee', team: '#a78bfa', enterprise: '#fbbf24' }[plan] || '#38bdf8',
                      }))} />
                    </div>
                  )}
                </>
              )}
                </>
              )}
            </div>
          )}

          {/* ════════ Audit Log ════════ */}
          {/* ════════ Daily Usage ════════ */}
          {activeTab === 'usage' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-white">{usageAllTime ? 'All-Time Usage' : 'Daily Usage'}</h2>
                  <p className="text-sm text-slate-400 mt-1">Per-user token usage, credits and estimated cost</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={usageDate}
                    max={new Date().toISOString().slice(0, 10)}
                    disabled={usageAllTime}
                    onChange={(e) => { setUsageDate(e.target.value); setUsageAllTime(false); }}
                    className="bg-slate-800/60 border border-slate-700/60 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/60 [color-scheme:dark] disabled:opacity-40"
                  />
                  <button
                    onClick={() => setUsageAllTime(v => !v)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      usageAllTime
                        ? 'bg-violet-500/15 text-violet-300 border-violet-500/40'
                        : 'bg-slate-800/40 text-slate-400 border-slate-700/60 hover:text-white'
                    }`}
                  >
                    All time
                  </button>
                  <button
                    onClick={fetchDailyUsage}
                    className="px-3 py-2 rounded-lg bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 text-sm font-medium hover:bg-cyan-500/20 transition-colors"
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {dailyUsageLoading ? (
                <div className="flex items-center gap-3 text-slate-400">
                  <div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
                  Loading usage...
                </div>
              ) : !dailyUsage ? null : (
                <>
                  {/* Day totals */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: 'Active users', num: dailyUsage.totals.users },
                      { label: 'Requests', num: dailyUsage.totals.requests },
                      { label: 'Input tokens', num: dailyUsage.totals.input_tokens },
                      { label: 'Output tokens', num: dailyUsage.totals.output_tokens },
                      { label: 'Cached tokens', num: dailyUsage.totals.cached_tokens },
                      { label: 'Cache hit', num: dailyUsage.totals.cache_hit_pct ?? 0, raw: true, decimals: 1, suffix: '%' },
                      { label: 'Total tokens', num: dailyUsage.totals.tokens },
                      { label: 'Credits', num: dailyUsage.totals.credits ?? 0, raw: true, decimals: 1, suffix: ' cre' },
                      { label: 'Est. cost', num: dailyUsage.totals.est_cost_usd ?? 0, raw: true, decimals: 4, prefix: '$' },
                    ].map((c) => (
                      <div key={c.label} className="dl-card rounded-2xl p-4">
                        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{c.label}</div>
                        <div className="text-2xl font-bold text-white mt-1 tabular-nums">
                          <CountUp value={c.num} raw={c.raw} decimals={c.decimals || 0} prefix={c.prefix || ''} suffix={c.suffix || ''} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Per-user table */}
                  {dailyUsage.users.length === 0 ? (
                    <div className="dl-card rounded-2xl p-12 text-center">
                      <Activity className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No usage recorded {dailyUsage.date === 'all' ? 'ever' : `on ${dailyUsage.date}`}.</p>
                    </div>
                  ) : (
                    <div className="dl-card rounded-2xl overflow-hidden">
                      <div className="admin-scroll overflow-y-auto overflow-x-auto" style={{ maxHeight: 'calc(100vh - 340px)' }}>
                        <table className="w-full" style={{ minWidth: '760px' }}>
                          <thead className="sticky top-0 z-10" style={{ background: 'rgba(13, 17, 28, 0.98)', backdropFilter: 'blur(8px)' }}>
                            <tr className="border-b border-slate-700/50">
                              <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">User</th>
                              <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Plan</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Reqs</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">In</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Out</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Cached</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Hit %</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Est. cost</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Credits</th>
                              <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Models</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800/40">
                            {dailyUsage.users.map((u) => (
                              <tr key={u.user_id} className="hover:bg-slate-800/30 transition-colors">
                                <td className="px-5 py-3">
                                  <div className="text-xs font-medium text-white">{u.name || u.email}</div>
                                  {u.name && <div className="text-[10px] text-slate-500">{u.email}</div>}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${PLAN_BADGE[u.plan] || PLAN_BADGE.free}`}>
                                    {u.plan}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right text-xs text-slate-300 tabular-nums">{u.requests}</td>
                                <td className="px-4 py-3 text-right text-xs text-slate-400 tabular-nums">{formatTokens(u.input_tokens)}</td>
                                <td className="px-4 py-3 text-right text-xs text-slate-400 tabular-nums">{formatTokens(u.output_tokens)}</td>
                                <td className="px-4 py-3 text-right text-xs font-medium text-white tabular-nums">{formatTokens(u.tokens)}</td>
                                <td className="px-4 py-3 text-right text-xs text-slate-500 tabular-nums">{formatTokens(u.cached_tokens)}</td>
                                <td className={`px-4 py-3 text-right text-xs tabular-nums font-medium ${u.cache_hit_pct >= 70 ? 'text-emerald-400' : u.cache_hit_pct >= 40 ? 'text-amber-400' : 'text-slate-400'}`}>{u.cache_hit_pct}%</td>
                                <td className="px-4 py-3 text-right text-xs font-semibold text-cyan-400 tabular-nums">{formatCost(u.est_cost_usd)}</td>
                                <td className="px-4 py-3 text-right text-xs font-semibold text-violet-400 tabular-nums">{(u.credits ?? 0).toFixed(1)}</td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap gap-1">
                                    {Object.keys(u.models).slice(0, 3).map((m) => (
                                      <span key={m} className="px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-400 text-[10px]">{m}</span>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── Visual breakdown ── */}
                  {dailyUsage.users.length > 0 && (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="dl-card rounded-2xl p-5">
                        <h3 className="text-sm font-semibold text-slate-300 mb-4">Token mix — {dailyUsage.date}</h3>
                        <StackedBar parts={[
                          { label: 'Input (uncached)', value: Math.max(0, dailyUsage.totals.input_tokens - dailyUsage.totals.cached_tokens), color: '#38bdf8' },
                          { label: 'Cache hits', value: dailyUsage.totals.cached_tokens, color: '#34d399' },
                          { label: 'Output', value: dailyUsage.totals.output_tokens, color: '#a78bfa' },
                        ]} />
                        <p className="text-[10px] text-slate-600 mt-3">
                          {dailyUsage.totals.cache_hit_pct}% of input served from cache — higher = cheaper & faster
                        </p>
                      </div>
                      <div className="dl-card rounded-2xl p-5">
                        <h3 className="text-sm font-semibold text-slate-300 mb-4">Top users by tokens</h3>
                        <div className="space-y-2">
                          {dailyUsage.users.slice(0, 8).map((u) => (
                            <HBar
                              key={u.user_id}
                              label={u.name || u.email}
                              value={u.tokens}
                              max={dailyUsage.users[0]?.tokens || 1}
                              color="#22d3ee"
                              sub={formatTokens(u.tokens)}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ════════ Usage Quality ════════ */}
          {activeTab === 'quality' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-white">Usage Quality</h2>
                  <p className="text-sm text-slate-400 mt-1">
                    Who's doing real work vs burning tokens — conversation outcomes, edit throughput, loop/waste signals
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                    <input
                      type="text"
                      value={qualitySearch}
                      onChange={(e) => setQualitySearch(e.target.value)}
                      placeholder="Search user…"
                      className="bg-slate-800/60 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-200 w-44 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <select
                    value={qualitySort}
                    onChange={(e) => setQualitySort(e.target.value)}
                    className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-500"
                  >
                    <option value="verdict">Sort: Verdict</option>
                    <option value="prompting">Sort: Prompting</option>
                    <option value="cost">Sort: Cost</option>
                    <option value="tokens">Sort: Tokens</option>
                    <option value="conversations">Sort: Conversations</option>
                    <option value="completion">Sort: Completion</option>
                  </select>
                  <select
                    value={qualityDays}
                    onChange={(e) => setQualityDays(Number(e.target.value))}
                    className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-500"
                  >
                    {[1, 3, 7, 14, 30, 90].map(d => <option key={d} value={d}>Last {d}d</option>)}
                  </select>
                  <button
                    onClick={fetchQuality}
                    disabled={qualityLoading}
                    className="px-4 py-2 rounded-lg bg-cyan-600/20 text-cyan-400 text-sm font-medium hover:bg-cyan-600/30 disabled:opacity-50 transition-colors"
                  >
                    {qualityLoading ? 'Scanning…' : 'Refresh'}
                  </button>
                </div>
              </div>

              {qualityLoading ? (
                <div className="flex items-center gap-3 text-slate-400">
                  <div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
                  Scanning usage events + conversation logs (large windows can take a while)…
                </div>
              ) : !qualityData ? null : (
                <>
                  {/* Window totals */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: 'Users seen', value: qualityData.totals.users },
                      { label: 'Requests', value: qualityData.totals.requests },
                      { label: 'Conversations', value: qualityData.totals.conversations },
                      { label: 'Credits', value: `${(qualityData.totals.credits ?? 0).toFixed(1)} cre` },
                      { label: 'Est. cost', value: formatCost(qualityData.totals.est_cost_usd) },
                    ].map((c) => (
                      <div key={c.label} className="dl-card rounded-2xl p-4">
                        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{c.label}</div>
                        <div className="text-2xl font-bold text-white mt-1 tabular-nums">{c.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Rating distribution */}
                  {qualityData.users.length > 0 && (
                    <div className="dl-card rounded-2xl px-5 py-3 flex items-center gap-6 flex-wrap">
                      <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Verdicts</span>
                      {[
                        { k: 'efficient', label: 'Efficient', cls: 'text-emerald-400' },
                        { k: 'good', label: 'Good', cls: 'text-cyan-400' },
                        { k: 'mixed', label: 'Mixed', cls: 'text-amber-400' },
                        { k: 'wasteful', label: 'Wasteful', cls: 'text-red-400' },
                        { k: 'suspicious', label: 'Suspicious', cls: 'text-red-300' },
                        { k: 'unrated', label: 'No data', cls: 'text-slate-500' },
                      ].map(({ k, label, cls }) => {
                        const n = qualityData.users.filter(u => u.rating === k).length;
                        return n > 0 ? <span key={k} className={`text-sm font-semibold ${cls} tabular-nums`}>{n} {label}</span> : null;
                      })}
                    </div>
                  )}

                  {qualityData.users.length === 0 ? (
                    <div className="dl-card rounded-2xl p-12 text-center">
                      <Activity className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No usage in the last {qualityData.days} day{qualityData.days === 1 ? '' : 's'}.</p>
                    </div>
                  ) : (
                    <div className="dl-card rounded-2xl overflow-hidden">
                      <div className="admin-scroll overflow-y-auto overflow-x-auto" style={{ maxHeight: 'calc(100vh - 360px)' }}>
                        <table className="w-full" style={{ minWidth: '1240px' }}>
                          <thead className="sticky top-0 z-10" style={{ background: 'rgba(13, 17, 28, 0.98)', backdropFilter: 'blur(8px)' }}>
                            <tr className="border-b border-slate-700/50">
                              <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">User</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider" title="Conversations / total agent steps">Convs·Steps</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider" title="File writes/edits — output that landed">Edits</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider" title="Edits per 1K input tokens — higher = more work per token">Ed/K</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider" title="File re-reads — same file read 2+ times in a conversation">Re-reads</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider" title="Consecutive identical tool calls — stuck-loop signal">Dup calls</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider" title="Conversations ending in a completed final step">Done</th>
                              <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider" title="Prompt-engineering skill: specificity, scoping, first-try completion, few corrections">Prompting</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Cache</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider" title="Tokens (input + output)">Tokens</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider" title="Quota units — 1 credit = $0.01 API cost">Credits</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Est. cost</th>
                              <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider" title="Cost per completed conversation">$/task</th>
                              <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider" style={{ minWidth: '240px' }}>Verdict</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800/40">
                            {qualityRows.length === 0 && (
                              <tr><td colSpan={14} className="px-5 py-8 text-center text-slate-500 text-sm">No users match "{qualitySearch}".</td></tr>
                            )}
                            {qualityRows.map((u) => (
                              <tr key={u.user} className="hover:bg-slate-800/30 transition-colors">
                                <td className="px-5 py-3">
                                  <div className="text-xs font-medium text-white">{u.name || u.user}</div>
                                  {u.email && <div className="text-[10px] text-slate-500">{u.email}</div>}
                                  {u.plan && <span className={`inline-block mt-0.5 px-1.5 py-0 rounded text-[9px] font-semibold ${PLAN_BADGE[u.plan] || PLAN_BADGE.free}`}>{u.plan}</span>}
                                </td>
                                <td className="px-4 py-3 text-right text-xs text-slate-300 tabular-nums">
                                  {u.conversations}<span className="text-slate-600"> · </span><span className="text-slate-500">{u.steps}</span>
                                </td>
                                <td className="px-4 py-3 text-right text-xs font-medium text-white tabular-nums">{u.edits}</td>
                                <td className="px-4 py-3 text-right text-xs tabular-nums">
                                  <span className={u.edits_per_1k_tokens >= 0.5 ? 'text-emerald-400' : u.edits_per_1k_tokens >= 0.1 ? 'text-slate-300' : 'text-slate-500'}>
                                    {u.edits_per_1k_tokens}
                                  </span>
                                </td>
                                <td className={`px-4 py-3 text-right text-xs tabular-nums ${u.rereads > 10 ? 'text-amber-400' : 'text-slate-400'}`}>{u.rereads}</td>
                                <td className={`px-4 py-3 text-right text-xs tabular-nums ${u.dup_tool_calls > 4 ? 'text-amber-400' : 'text-slate-400'}`}>{u.dup_tool_calls}</td>
                                <td className={`px-4 py-3 text-right text-xs tabular-nums ${u.completion_pct >= 70 ? 'text-emerald-400' : u.completion_pct >= 40 ? 'text-slate-300' : 'text-red-400'}`}>
                                  {u.completion_pct}%
                                </td>
                                <td className="px-4 py-3" title={u.prompt_reason || ''}>
                                  {u.prompt_score >= 0 ? (
                                    <>
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                                        u.prompt_label === 'precise' ? 'bg-emerald-500/15 text-emerald-400' :
                                        u.prompt_label === 'clear' ? 'bg-cyan-500/15 text-cyan-400' :
                                        u.prompt_label === 'basic' ? 'bg-amber-500/15 text-amber-400' :
                                        'bg-red-500/15 text-red-400'
                                      }`}>{u.prompt_label}</span>
                                      <span className="text-[10px] text-slate-500 tabular-nums ml-1">{u.prompt_score}/100</span>
                                    </>
                                  ) : <span className="text-[10px] text-slate-600">—</span>}
                                </td>
                                <td className={`px-4 py-3 text-right text-xs tabular-nums ${u.cache_hit_pct >= 70 ? 'text-emerald-400' : u.cache_hit_pct >= 40 ? 'text-amber-400' : 'text-slate-400'}`}>
                                  {u.cache_hit_pct}%
                                </td>
                                <td className="px-4 py-3 text-right text-xs text-slate-300 tabular-nums">{formatTokens(u.input_tokens + u.output_tokens)}</td>
                                <td className="px-4 py-3 text-right text-xs font-semibold text-violet-400 tabular-nums">{(u.credits ?? 0).toFixed(1)}</td>
                                <td className="px-4 py-3 text-right text-xs font-semibold text-cyan-400 tabular-nums">{formatCost(u.est_cost_usd)}</td>
                                <td className="px-4 py-3 text-right text-xs text-slate-300 tabular-nums">
                                  {u.cost_per_completed_task != null ? formatCost(u.cost_per_completed_task) : '—'}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                                      u.rating === 'efficient' ? 'bg-emerald-500/15 text-emerald-400' :
                                      u.rating === 'good' ? 'bg-cyan-500/15 text-cyan-400' :
                                      u.rating === 'mixed' ? 'bg-amber-500/15 text-amber-400' :
                                      u.rating === 'wasteful' ? 'bg-red-500/15 text-red-400' :
                                      u.rating === 'suspicious' ? 'bg-red-500/20 text-red-300' :
                                      'bg-slate-700/40 text-slate-500'
                                    }`}>{u.rating}</span>
                                    {u.score >= 0 && <span className="text-[10px] text-slate-500 tabular-nums">{u.score}/100</span>}
                                  </div>
                                  <div className="text-[10px] text-slate-400 mt-1 leading-snug">{u.reason}</div>
                                  {u.flags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {u.flags.map((f) => (
                                        <span key={f} className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                                          f === 'heavy' ? 'bg-red-500/10 text-red-400' :
                                          f === 'scripted' ? 'bg-red-500/10 text-red-400' :
                                          f === 'low_output' ? 'bg-orange-500/10 text-orange-400' :
                                          f === 'loop_prone' ? 'bg-amber-500/10 text-amber-400' :
                                          f === 'churn' ? 'bg-amber-500/10 text-amber-400' :
                                          'bg-purple-500/10 text-purple-400'
                                        }`}>{f.replace('_', ' ')}</span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Flag legend */}
                  <div className="dl-card rounded-2xl p-4 text-[11px] text-slate-500 flex flex-wrap gap-x-6 gap-y-1">
                    <span><b className="text-red-400">heavy</b> — &gt;25% of total spend</span>
                    <span><b className="text-amber-400">loop prone</b> — repeated identical tool calls / file re-reads</span>
                    <span><b className="text-orange-400">low output</b> — real spend, zero file edits</span>
                    <span><b className="text-amber-400">churn</b> — same prompt re-sent across conversations</span>
                    <span><b className="text-red-400">scripted</b> — &gt;50% of requests under 3s apart</span>
                    <span><b className="text-purple-400">overkill</b> — tiny prompts on frontier models</span>
                    <span className="text-slate-600">|</span>
                    <span><b className="text-slate-400">prompting</b> — precise/clear/basic/vague from specificity, scoping &amp; first-try rate</span>
                  </div>

                  {/* ── Visual summary ── */}
                  {qualityData.users.length > 0 && (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="dl-card rounded-2xl p-5">
                        <h3 className="text-sm font-semibold text-slate-300 mb-4">Verdict distribution</h3>
                        <StackedBar parts={[
                          { label: 'Efficient', value: qualityData.users.filter(u => u.rating === 'efficient').length, color: '#34d399' },
                          { label: 'Good', value: qualityData.users.filter(u => u.rating === 'good').length, color: '#22d3ee' },
                          { label: 'Mixed', value: qualityData.users.filter(u => u.rating === 'mixed').length, color: '#fbbf24' },
                          { label: 'Wasteful', value: qualityData.users.filter(u => u.rating === 'wasteful').length, color: '#f87171' },
                          { label: 'Suspicious', value: qualityData.users.filter(u => u.rating === 'suspicious').length, color: '#ef4444' },
                          { label: 'No data', value: qualityData.users.filter(u => u.rating === 'unrated').length, color: '#475569' },
                        ]} />
                      </div>
                      <div className="dl-card rounded-2xl p-5">
                        <h3 className="text-sm font-semibold text-slate-300 mb-4">Score leaderboard</h3>
                        <div className="space-y-2">
                          {[...qualityData.users]
                            .filter(u => u.score >= 0)
                            .sort((a, b) => b.score - a.score)
                            .slice(0, 8)
                            .map((u) => (
                              <HBar
                                key={u.user}
                                label={u.name || u.user}
                                value={u.score}
                                max={100}
                                color={u.rating === 'efficient' ? '#34d399' : u.rating === 'good' ? '#22d3ee' : u.rating === 'mixed' ? '#fbbf24' : '#f87171'}
                                sub={`${u.score}/100`}
                              />
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="space-y-6 w-full max-w-7xl mx-auto">
              <div>
                <h2 className="text-2xl font-bold text-white">Audit Log</h2>
                <p className="text-sm text-slate-400 mt-1">Every admin action — who, what, when</p>
              </div>
              {loading ? (
                <div className="flex items-center gap-3 text-slate-400">
                  <div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
                  Loading...
                </div>
              ) : auditLog.length === 0 ? (
                <div className="dl-card rounded-2xl p-12 text-center">
                  <Shield className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No admin actions recorded yet.</p>
                </div>
              ) : (
                <div className="dl-card rounded-2xl overflow-hidden">
                  <div className="admin-scroll overflow-y-auto overflow-x-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                    <table className="w-full" style={{ minWidth: '700px' }}>
                      <thead className="sticky top-0 z-10" style={{ background: 'rgba(13, 17, 28, 0.98)', backdropFilter: 'blur(8px)' }}>
                        <tr className="border-b border-slate-700/50">
                          <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap w-44">Time</th>
                          <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap w-48">Actor</th>
                          <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap w-36">Action</th>
                          <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap w-44">Target</th>
                          <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/40">
                        {auditLog.map((a, i) => (
                          <tr key={i} className="hover:bg-slate-800/30 transition-colors align-top">
                            <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap tabular-nums">
                              {a.timestamp ? new Date(a.timestamp).toLocaleString() : '—'}
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs font-medium text-cyan-400 break-all">{a.actor}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs text-white whitespace-nowrap">{a.action.replace(/_/g, ' ')}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs text-slate-300 break-all">{a.target_label || a.target}</span>
                              {a.target_label && a.target_label !== a.target && (
                                <span className="text-[10px] text-slate-600 font-mono block break-all">{a.target}</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs text-slate-500 break-all">
                                {a.details && Object.keys(a.details).length > 0 ? JSON.stringify(a.details) : '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════════ Contacts ════════ */}
          {activeTab === 'contacts' && (
          <div className="space-y-6 w-full max-w-7xl mx-auto">
            <div>
              <h2 className="text-2xl font-bold text-white">Contact Submissions</h2>
              <p className="text-sm text-slate-400 mt-1">Messages from the contact form on the landing page</p>
            </div>

            {contactsLoading ? (
              <div className="flex items-center gap-3 text-slate-400">
                <div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
                Loading...
              </div>
            ) : contacts.length === 0 ? (
              <div className="dl-card rounded-2xl p-12 text-center">
                <Mail className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No contact submissions yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contacts.map((c) => (
                  <div key={c._id || c.email + c.created_at} className="dl-card rounded-2xl p-5">
                    {/* Header row: name + status + actions */}
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                          <MessageSquare className="w-5 h-5 text-blue-400" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-bold text-white block truncate">{c.name}</span>
                          <span className="text-xs text-slate-500 truncate">{c.email}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          c.status === 'new' ? 'bg-green-500/10 text-green-400' :
                          c.status === 'read' ? 'bg-blue-500/10 text-blue-400' :
                          c.status === 'responded' ? 'bg-cyan-500/10 text-cyan-400' :
                          'bg-slate-700/40 text-slate-500'
                        }`}>
                          {c.status || 'new'}
                        </span>
                        {c.status === 'new' && (
                          <button
                            onClick={async () => {
                              try {
                                await apiCall(`/contact/submissions/${c._id}/status?status=read`, { method: 'POST' });
                                fetchContacts();
                              } catch (err) { setError(err.message); }
                            }}
                            className="px-3 py-1.5 text-xs text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors font-medium"
                          >
                            Mark Read
                          </button>
                        )}
                        <a
                          href={`mailto:${c.email}?subject=Re: Your inquiry to ChipDesigner.ai&body=Hi ${c.name},%0D%0A%0D%0AThank you for reaching out.%0D%0A%0D%0A`}
                          className="px-3 py-1.5 text-xs text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors font-medium"
                        >
                          Reply
                        </a>
                      </div>
                    </div>

                    {/* Meta row: company + seats */}
                    <div className="flex items-center gap-2 flex-wrap pl-[52px] mb-3">
                      {c.company && <span className="text-xs text-slate-400">· {c.company}</span>}
                      {c.seats && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-500/10 text-blue-400">{c.seats} seats</span>}
                    </div>

                    {/* Message */}
                    <p className="text-sm text-slate-300 pl-[52px]">{c.message}</p>

                    {/* Timestamp */}
                    <p className="text-xs text-slate-600 mt-2 pl-[52px]">
                      {c.created_at ? new Date(c.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <LogsTab headers={adminHeaders} />
        )}
        </div>

        <footer className="border-t border-cyan-500/10 py-4 px-6 shrink-0" style={{ background: 'linear-gradient(to top, rgba(10, 16, 32, 0.6) 0%, transparent 100%)' }}>
          <p className="text-xs text-slate-500 text-center">&copy; 2026 chipdesigner.ai Admin · All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => {
  const colors = {
    blue: 'from-blue-600/15 to-blue-500/5 text-blue-400',
    cyan: 'from-cyan-600/15 to-cyan-500/5 text-cyan-400',
    green: 'from-green-600/15 to-green-500/5 text-green-400',
    purple: 'from-purple-600/15 to-purple-500/5 text-purple-400',
    slate: 'from-slate-600/15 to-slate-500/5 text-slate-400',
  };
  return (
    <div className={`dl-card rounded-2xl p-5 bg-gradient-to-br ${colors[color] || colors.slate}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-slate-400">{label}</p>
        {Icon && <Icon className="w-4 h-4 opacity-50" />}
      </div>
      <p className="text-3xl font-bold tabular-nums">
        {typeof value === 'number' ? <CountUp value={value} /> : value}
      </p>
    </div>
  );
};

const LEVEL_COLORS = {
  ERROR: 'text-red-400',
  CRITICAL: 'text-red-500',
  WARNING: 'text-amber-400',
  INFO: 'text-slate-300',
  DEBUG: 'text-slate-500',
};

const LogsTab = ({ headers }) => {
  const [entries, setEntries] = useState([]);
  const [live, setLive] = useState(true);
  const [follow, setFollow] = useState(true);
  const [filter, setFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [status, setStatus] = useState('connecting'); // connecting | live | reconnecting | paused | error
  const boxRef = useRef(null);
  const entriesRef = useRef([]);

  const pushBatch = (batch) => {
    entriesRef.current = [...entriesRef.current, ...batch].slice(-1500);
    setEntries(entriesRef.current);
  };

  // Initial snapshot — last 400 buffered lines
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/logs?lines=400`, { headers });
        if (!res.ok) { setStatus('error'); return; }
        const data = await res.json();
        if (!cancelled) pushBatch(data.lines || []);
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live SSE stream via fetch (EventSource can't send auth headers)
  useEffect(() => {
    if (!live) { setStatus('paused'); return; }
    const abort = new AbortController();
    let buf = '';
    (async () => {
      while (!abort.signal.aborted) {
        try {
          setStatus((s) => (s === 'live' ? s : 'connecting'));
          const res = await fetch(`${API_BASE}/api/admin/logs/stream`, {
            headers, signal: abort.signal,
          });
          if (!res.ok || !res.body) { setStatus('error'); return; }
          setStatus('live');
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const parts = buf.split('\n\n');
            buf = parts.pop() || '';
            const batch = [];
            for (const p of parts) {
              const m = p.match(/^data: (.*)$/m);
              if (m) { try { batch.push(JSON.parse(m[1])); } catch { /* skip */ } }
            }
            if (batch.length) pushBatch(batch);
          }
          if (!abort.signal.aborted) setStatus('reconnecting');
        } catch {
          if (!abort.signal.aborted) setStatus('reconnecting');
        }
        if (!abort.signal.aborted) {
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
    })();
    return () => abort.abort();
  }, [live]);

  // Auto-scroll to bottom while following
  useEffect(() => {
    if (follow && boxRef.current) {
      boxRef.current.scrollTop = boxRef.current.scrollHeight;
    }
  }, [entries, follow]);

  const visible = entries.filter((e) =>
    (levelFilter === 'ALL' || e.level === levelFilter) &&
    (!filter || e.line.toLowerCase().includes(filter.toLowerCase()))
  );

  const statusBadge = {
    live: 'bg-green-500/10 text-green-400',
    connecting: 'bg-amber-500/10 text-amber-400',
    reconnecting: 'bg-amber-500/10 text-amber-400',
    paused: 'bg-slate-600/30 text-slate-400',
    error: 'bg-red-500/10 text-red-400',
  }[status];

  return (
    <div className="space-y-4 w-full max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Backend Logs</h2>
          <p className="text-sm text-slate-400 mt-1">
            Live stream from the backend process — same output as <code className="text-cyan-400">docker logs -f</code>
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge}`}>
          {status === 'live' ? '● live' : status}
        </span>
      </div>

      {/* Toolbar */}
      <div className="dl-card rounded-2xl p-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter logs (e.g. Quota, ERROR, user email)…"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-900/60 border border-slate-700/60 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
          />
        </div>
        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          className="px-3 py-2 rounded-xl bg-slate-900/60 border border-slate-700/60 text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
        >
          {['ALL', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'].map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
        <button
          onClick={() => setLive((v) => !v)}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            live ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                 : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
          }`}
        >
          {live ? 'Pause' : 'Resume'}
        </button>
        <button
          onClick={() => setFollow((v) => !v)}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            follow ? 'bg-cyan-500/10 text-cyan-400' : 'bg-slate-700/40 text-slate-400'
          }`}
        >
          {follow ? 'Following' : 'Follow'}
        </button>
        <button
          onClick={() => { entriesRef.current = []; setEntries([]); }}
          className="px-4 py-2 rounded-xl text-sm font-medium bg-slate-700/40 text-slate-400 hover:bg-slate-700/60 transition-colors"
        >
          Clear
        </button>
        <span className="text-xs text-slate-500 ml-auto">{visible.length} lines</span>
      </div>

      {/* Log console */}
      <div
        ref={boxRef}
        className="dl-card rounded-2xl p-4 h-[60vh] overflow-y-auto font-mono text-xs leading-5 admin-scroll"
        style={{ background: 'rgba(5, 8, 16, 0.85)' }}
      >
        {visible.length === 0 ? (
          <p className="text-slate-500">No log lines{filter || levelFilter !== 'ALL' ? ' matching filter' : ''}.</p>
        ) : visible.map((e) => (
          <div key={e.seq} className="flex gap-3 whitespace-pre-wrap break-all hover:bg-slate-800/30 px-1 rounded">
            <span className="text-slate-600 shrink-0 tabular-nums">{e.ts}</span>
            <span className={`shrink-0 w-16 ${LEVEL_COLORS[e.level] || 'text-slate-400'}`}>{e.level}</span>
            <span className="text-slate-300">{e.line}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPage;
